/**
 * County-level easement guides.
 *
 * High-intent counties where people search for "easement" plus a place name.
 * Each entry carries only facts that are stable and publicly verifiable: which
 * office records instruments, whether the county publishes a public parcel
 * viewer, and the easement types that dominate local title work. The
 * prescriptive-use period is never duplicated here, it is read from the state
 * law dataset so there is one source of truth.
 *
 * No paid data provider is used or referenced.
 */

export interface CountyEasementGuide {
  /** URL slug within the state, e.g. "maricopa". */
  slug: string;
  /** Display name, e.g. "Maricopa County". */
  county: string;
  stateCode: string;
  stateName: string;
  /** Largest city or metro people associate with the county. */
  metro: string;
  /** Office that records deeds and easements. */
  recordingOffice: string;
  /** True when the county publishes a free public parcel/GIS viewer. */
  publicGis: boolean;
  /** One-paragraph orientation for this county. */
  intro: string;
  /** Easement types that show up most often in local title commitments. */
  commonEasements: string[];
  /** Practical, county-specific pointers. */
  localNotes: string[];
  /** Official website of the recording office, when published. */
  recorderUrl?: string;
  /** Official page where that office publishes its current fee schedule. */
  feeScheduleUrl?: string;
  /**
   * Short note about how filing fees are structured locally. Never a quoted
   * dollar amount unless it is set by statute, published schedules change,
   * so the link above is the authority.
   */
  feeNote?: string;
}

export const COUNTY_EASEMENT_GUIDES: CountyEasementGuide[] = [
  {
    slug: "maricopa",
    county: "Maricopa County",
    stateCode: "AZ",
    stateName: "Arizona",
    metro: "Phoenix",
    recordingOffice: "Maricopa County Recorder",
    publicGis: true,
    intro:
      "Fast subdivision growth around Phoenix means most Maricopa lots carry platted utility and drainage easements along every lot line, plus irrigation rights that predate the subdivision.",
    commonEasements: [
      "Public utility easements along front and rear lot lines",
      "Drainage and retention easements on desert subdivisions",
      "Irrigation district ditch and canal easements",
      "Shared driveway and access easements on flag lots",
    ],
    recorderUrl: "https://recorder.maricopa.gov/",
    feeNote:
      "Arizona sets recording fees by statute at a flat rate per document rather than per page, so a multi-page easement costs the same as a one-page one. The Recorder publishes the current amount.",
    localNotes: [
      "Older parcels often carry irrigation easements that never appear on the subdivision plat. Check the deed chain, not just the plat.",
      "Retention basin easements can cover a large share of a corner lot's rear yard.",
    ],
  },
  {
    slug: "travis",
    county: "Travis County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Austin",
    recordingOffice: "Travis County Clerk",
    publicGis: true,
    intro:
      "Hill Country topography and rapid infill make Travis County title work heavy on drainage, slope, and shared-access easements, with electric transmission corridors crossing the outer county.",
    commonEasements: [
      "Electric transmission and distribution easements",
      "Drainage and water-quality buffer easements",
      "Slope and construction easements on hillside lots",
      "Private road and shared access easements outside city limits",
    ],
    recorderUrl: "https://countyclerk.traviscountytx.gov/",
    feeNote:
      "Texas county clerks charge a first-page fee plus a smaller per-page fee, so long metes-and-bounds easements cost more to record. The Clerk publishes the current schedule.",
    localNotes: [
      "Water-quality buffers near creeks can restrict building even where the easement itself is narrow.",
      "Unincorporated tracts frequently rely on a private road easement rather than a public street.",
    ],
  },
  {
    slug: "harris",
    county: "Harris County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Houston",
    recordingOffice: "Harris County Clerk",
    publicGis: true,
    intro:
      "Flat terrain and heavy rainfall put drainage at the center of Harris County easements, alongside one of the densest pipeline networks in the country.",
    commonEasements: [
      "Storm sewer and drainage easements",
      "Oil, gas, and product pipeline easements",
      "Utility easements along rear lot lines",
      "Detention pond easements in newer subdivisions",
    ],
    recorderUrl: "https://www.cclerk.hctx.net/applications/websearch/RP.aspx",
    feeNote:
      "Texas county clerks charge a first-page fee plus a per-page fee; exhibits and survey sheets each count as pages. The Clerk publishes the current schedule.",
    localNotes: [
      "Pipeline corridors often carry building setbacks wider than the recorded easement itself.",
      "Drainage district easements may be held by a district rather than the county.",
    ],
  },
  {
    slug: "clark",
    county: "Clark County",
    stateCode: "NV",
    stateName: "Nevada",
    metro: "Las Vegas",
    recordingOffice: "Clark County Recorder",
    publicGis: true,
    intro:
      "Master-planned communities dominate Clark County, so easements are usually platted and uniform, but federal land patents on the valley edges add reservation language that surprises buyers.",
    commonEasements: [
      "Public utility easements shown on the recorded plat",
      "Flood control channel easements",
      "Federal land patent reservations for ditches and canals",
      "Common-area and CC&R access easements",
    ],
    recorderUrl: "https://www.clarkcountynv.gov/government/elected_officials/county_recorder/index.php",
    feeNote:
      "Nevada recording fees are a per-page charge plus statutory add-ons, and a separate technology fee applies to most real property instruments. The Recorder publishes the current schedule.",
    localNotes: [
      "Patent reservations under the 1866 ditch-and-canal acts can appear on parcels near former federal land.",
      "Flood control easements are often held by the regional district, not the county.",
    ],
  },
  {
    slug: "king",
    county: "King County",
    stateCode: "WA",
    stateName: "Washington",
    metro: "Seattle",
    recordingOffice: "King County Recorder's Office",
    publicGis: true,
    intro:
      "Steep lots, dense infill, and a long rail history give King County a mix of shared-driveway easements, sewer easements, and rail-corridor conversions.",
    commonEasements: [
      "Shared driveway and ingress/egress easements",
      "Side sewer easements crossing neighboring lots",
      "Utility easements for power and fiber",
      "Rail-to-trail corridor easements",
    ],
    recorderUrl: "https://kingcounty.gov/en/legacy/depts/records-licensing/recorders-office.aspx",
    feeNote:
      "Washington charges a single flat recording fee per document that already covers the extra pages, plus state surcharges. The Recorder publishes the current amount.",
    localNotes: [
      "Side sewer easements are common on older Seattle blocks and are frequently unrecorded, a survey often catches them first.",
      "Critical-area buffers act like easements in practice even when they are zoning, not title.",
    ],
  },
  {
    slug: "los-angeles",
    county: "Los Angeles County",
    stateCode: "CA",
    stateName: "California",
    metro: "Los Angeles",
    recordingOffice: "Los Angeles County Registrar-Recorder/County Clerk",
    publicGis: true,
    intro:
      "Century-old subdivisions layered with modern utility upgrades mean many Los Angeles County parcels carry easements recorded long before the current house existed.",
    commonEasements: [
      "Overhead and underground utility easements",
      "Storm drain and flood control easements",
      "Shared driveway easements on hillside lots",
      "Oil and mineral rights with surface entry language",
    ],
    recorderUrl: "https://www.lavote.gov/home/records/real-estate-records",
    feeNote:
      "California charges a first-page fee plus a per-page fee, and most easement recordings also carry the state Building Homes and Jobs Act surcharge unless an exemption is claimed on the face of the document.",
    localNotes: [
      "Mineral reservations sometimes waive surface entry. Read the waiver, not just the reservation.",
      "Hillside parcels often depend on a recorded slope maintenance easement.",
    ],
  },
  {
    slug: "san-diego",
    county: "San Diego County",
    stateCode: "CA",
    stateName: "California",
    metro: "San Diego",
    recordingOffice: "San Diego County Assessor/Recorder/County Clerk",
    publicGis: true,
    intro:
      "Canyon topography and habitat conservation programs make open-space and biological easements as common as utility easements across San Diego County.",
    commonEasements: [
      "Open space and habitat conservation easements",
      "Utility and communication easements",
      "Private road easements in the backcountry",
      "Drainage easements through canyon lots",
    ],
    recorderUrl: "https://arcc.sdcounty.ca.gov/",
    feeNote:
      "California charges a first-page fee plus a per-page fee, and the Building Homes and Jobs Act surcharge applies to most easements unless an exemption is declared.",
    localNotes: [
      "Conservation easements can permanently prohibit grading on a large share of a lot.",
      "Backcountry access frequently depends on a chain of private road easements across several parcels.",
    ],
  },
  {
    slug: "riverside",
    county: "Riverside County",
    stateCode: "CA",
    stateName: "California",
    metro: "Riverside",
    recordingOffice: "Riverside County Assessor-County Clerk-Recorder",
    publicGis: true,
    intro:
      "Agricultural conversion and long transmission corridors define Riverside County easements, especially in the fast-growing western and desert areas.",
    commonEasements: [
      "Electric transmission line easements",
      "Irrigation and water district easements",
      "Flood control channel easements",
      "Access easements to landlocked agricultural parcels",
    ],
    recorderUrl: "https://www.rivcoacr.org/",
    feeNote:
      "California charges a first-page fee plus a per-page fee, plus the Building Homes and Jobs Act surcharge on most easement instruments.",
    localNotes: [
      "Water district easements can predate the subdivision and run diagonally across modern lots.",
      "Transmission corridors carry setback and vegetation limits beyond the easement strip.",
    ],
  },
  {
    slug: "orange",
    county: "Orange County",
    stateCode: "CA",
    stateName: "California",
    metro: "Anaheim",
    recordingOffice: "Orange County Clerk-Recorder",
    publicGis: true,
    intro:
      "Nearly all of Orange County is platted, so easements are typically shown on the tract map, the exceptions are older ranch parcels and flood control takings.",
    commonEasements: [
      "Tract map utility easements",
      "Flood control and channel easements",
      "HOA common-area access easements",
      "Sewer easements crossing rear yards",
    ],
    recorderUrl: "https://www.ocrecorder.com/",
    feeNote:
      "California charges a first-page fee plus a per-page fee, plus the Building Homes and Jobs Act surcharge unless an exemption is stated on the document.",
    localNotes: [
      "The tract map is the fastest way to see easement geometry for most homes here.",
      "Older ranch splits may still rely on unrecorded historical access.",
    ],
  },
  {
    slug: "cook",
    county: "Cook County",
    stateCode: "IL",
    stateName: "Illinois",
    metro: "Chicago",
    recordingOffice: "Cook County Clerk's Office, Recordings Division",
    publicGis: true,
    intro:
      "Alleys, party walls, and rail corridors dominate Cook County easement questions, with the oldest instruments predating modern surveying standards.",
    commonEasements: [
      "Public alley and vacated alley easements",
      "Party wall and encroachment agreements",
      "Utility easements along rear lot lines",
      "Rail and transit corridor easements",
    ],
    recorderUrl: "https://www.cookcountyclerkil.gov/recordings",
    feeNote:
      "Cook County charges a predictable flat fee for standard real property documents, with a surcharge if the instrument does not meet the standard formatting rules. The Clerk publishes the current schedule.",
    localNotes: [
      "A vacated alley may have been split between neighbors with utility rights reserved.",
      "Party wall agreements function like easements and often go unread at closing.",
    ],
  },
  {
    slug: "dupage",
    county: "DuPage County",
    stateCode: "IL",
    stateName: "Illinois",
    metro: "Naperville",
    recordingOffice: "DuPage County Recorder",
    publicGis: true,
    intro:
      "Post-war subdivisions give DuPage County highly regular platted easements, with stormwater management easements added on nearly everything built after the 1990s.",
    commonEasements: [
      "Stormwater management and detention easements",
      "Public utility easements on the plat",
      "Sanitary sewer easements",
      "Bike path and trail easements",
    ],
    recorderUrl: "https://www.dupagecounty.gov/government/elected_officials/county_clerk/recordings/",
    feeNote:
      "Illinois counties use a flat predictable fee for standard documents, with non-conforming margins or page sizes triggering an extra charge.",
    localNotes: [
      "Stormwater easements often prohibit fences and sheds, not just structures.",
      "Detention areas may be jointly maintained by the HOA and the municipality.",
    ],
  },
  {
    slug: "miami-dade",
    county: "Miami-Dade County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Miami",
    recordingOffice: "Miami-Dade County Clerk of the Court and Comptroller",
    publicGis: true,
    intro:
      "Canal networks and dense platting make Miami-Dade easements predictable but restrictive, especially where a lot backs onto a drainage canal.",
    commonEasements: [
      "Canal and drainage district easements",
      "Utility easements on the plat",
      "Lake maintenance easements in newer communities",
      "Ingress/egress easements for interior lots",
    ],
    recorderUrl: "https://www.miamidadeclerk.gov/",
    feeNote:
      "Florida charges a first-page fee plus a per-page fee, set by statute statewide, and adds a small fee for each name indexed beyond the first two.",
    localNotes: [
      "Canal maintenance easements can extend a considerable distance inland from the water line.",
      "Lake maintenance easements typically bar permanent structures in the rear yard.",
    ],
  },
  {
    slug: "broward",
    county: "Broward County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Fort Lauderdale",
    recordingOffice: "Broward County Records, Taxes and Treasury Division",
    publicGis: true,
    intro:
      "Broward County lots are almost entirely platted, and water management easements are the single most common restriction homeowners run into.",
    commonEasements: [
      "Water management and drainage easements",
      "Utility easements along side and rear lot lines",
      "Lake maintenance easements",
      "Access easements for interior and flag lots",
    ],
    recorderUrl: "https://www.browardclerk.org/Divisions/OfficialRecords",
    feeNote:
      "Florida's statutory rate is a first-page fee plus a per-page fee, with an extra charge per indexed name beyond the first two.",
    localNotes: [
      "Water management districts hold easements separately from the county.",
      "Screen enclosures and pools are the usual sources of easement encroachment claims here.",
    ],
  },
  {
    slug: "hillsborough",
    county: "Hillsborough County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Tampa",
    recordingOffice: "Hillsborough County Clerk of Court",
    publicGis: true,
    intro:
      "Rapid suburban growth around Tampa layers new stormwater easements on top of older agricultural access and utility rights.",
    commonEasements: [
      "Stormwater and drainage easements",
      "Electric distribution easements",
      "Private road easements in unincorporated areas",
      "Conservation easements on wetland parcels",
    ],
    recorderUrl: "https://www.hillsclerk.com/Additional-Services/Official-Records",
    feeNote:
      "Florida's statutory rate is a first-page fee plus a per-page fee, plus a per-name indexing charge beyond the first two names.",
    localNotes: [
      "Wetland conservation easements often cover the rear third of a large lot.",
      "Unincorporated parcels may depend on a maintained-by-nobody private road easement.",
    ],
  },
  {
    slug: "orange-fl",
    county: "Orange County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Orlando",
    recordingOffice: "Orange County Comptroller, Official Records",
    publicGis: true,
    intro:
      "Master-planned growth around Orlando means most Orange County easements are platted, but community development districts add their own recorded rights.",
    commonEasements: [
      "Community development district utility easements",
      "Stormwater retention easements",
      "Public utility easements on the plat",
      "Access easements for shared driveways",
    ],
    recorderUrl: "https://myorangeclerk.com/",
    feeNote:
      "Florida's statutory rate is a first-page fee plus a per-page fee, plus an indexing charge for each name past the first two.",
    localNotes: [
      "District easements are recorded separately from the plat and are easy to miss.",
      "Retention pond easements frequently bar fencing.",
    ],
  },
  {
    slug: "fulton",
    county: "Fulton County",
    stateCode: "GA",
    stateName: "Georgia",
    metro: "Atlanta",
    recordingOffice: "Fulton County Clerk of Superior Court",
    publicGis: true,
    intro:
      "Wooded, sloped lots in and around Atlanta make sanitary sewer and stream buffer easements the recurring theme in Fulton County title work.",
    commonEasements: [
      "Sanitary sewer easements crossing rear yards",
      "Stream buffer and drainage easements",
      "Electric and gas utility easements",
      "Shared driveway easements on infill lots",
    ],
    recorderUrl: "https://www.fultonclerk.org/",
    feeNote:
      "Georgia sets a flat statutory filing fee per real estate instrument, the same whether the easement runs one page or ten.",
    localNotes: [
      "Sewer easements often run through the back of a lot with no visible surface marker.",
      "State stream buffers restrict clearing in addition to any recorded easement.",
    ],
  },
  {
    slug: "gwinnett",
    county: "Gwinnett County",
    stateCode: "GA",
    stateName: "Georgia",
    metro: "Lawrenceville",
    recordingOffice: "Gwinnett County Clerk of Superior Court",
    publicGis: true,
    intro:
      "Suburban Gwinnett is heavily subdivided, so most easements are platted utility and drainage strips, with detention facilities shared between neighbors.",
    commonEasements: [
      "Drainage and detention easements",
      "Public utility easements on the plat",
      "Sanitary sewer easements",
      "Sidewalk and right-of-way dedications",
    ],
    recorderUrl: "https://www.gwinnettcourts.com/",
    feeNote:
      "Georgia's statutory filing fee for a real estate instrument is a single flat amount regardless of page count.",
    localNotes: [
      "Detention easements shared by two lots can create maintenance disputes years later.",
      "Right-of-way dedications sometimes sit several feet behind the curb.",
    ],
  },
  {
    slug: "wake",
    county: "Wake County",
    stateCode: "NC",
    stateName: "North Carolina",
    metro: "Raleigh",
    recordingOffice: "Wake County Register of Deeds",
    publicGis: true,
    intro:
      "Wake County's growth pushes new subdivisions into former farmland, mixing modern platted easements with long-standing farm access rights.",
    commonEasements: [
      "Public utility easements on the recorded plat",
      "Stormwater control measure easements",
      "Greenway and trail easements",
      "Farm access easements on converted parcels",
    ],
    recorderUrl: "https://www.wake.gov/departments-government/register-deeds",
    feeNote:
      "North Carolina charges a base fee covering the first several pages plus a per-page fee after that, so a plat-heavy easement costs more.",
    localNotes: [
      "Greenway easements are often dedicated at subdivision and cross the rear of several lots.",
      "Stormwater device easements come with recorded maintenance obligations.",
    ],
  },
  {
    slug: "mecklenburg",
    county: "Mecklenburg County",
    stateCode: "NC",
    stateName: "North Carolina",
    metro: "Charlotte",
    recordingOffice: "Mecklenburg County Register of Deeds",
    publicGis: true,
    intro:
      "Charlotte-area infill and creek networks make storm drainage and sanitary sewer easements the dominant title issues in Mecklenburg County.",
    commonEasements: [
      "Storm drainage easements",
      "Sanitary sewer easements",
      "Utility easements for power and communications",
      "Access easements on subdivided infill lots",
    ],
    recorderUrl: "https://www.mecknc.gov/rod",
    feeNote:
      "North Carolina charges a base fee for the first pages of an instrument and a per-page fee beyond that; plats are priced separately.",
    localNotes: [
      "Floodplain overlays frequently coincide with drainage easements along creeks.",
      "Infill splits often create a new access easement that is not on the original plat.",
    ],
  },
  {
    slug: "franklin",
    county: "Franklin County",
    stateCode: "OH",
    stateName: "Ohio",
    metro: "Columbus",
    recordingOffice: "Franklin County Recorder",
    publicGis: true,
    intro:
      "Columbus-area subdivisions carry standard platted utility easements, while older parcels can still be crossed by rail and interurban corridors.",
    commonEasements: [
      "Public utility easements along lot lines",
      "Storm and sanitary sewer easements",
      "Former rail and interurban corridor easements",
      "Shared driveway easements in older neighborhoods",
    ],
    recorderUrl: "https://recorder.franklincountyohio.gov/",
    feeNote:
      "Ohio charges a first-two-pages fee plus a per-page fee after that, and requires a separate fee for each additional reference to a prior instrument.",
    localNotes: [
      "Abandoned rail corridors may have reverted, or may not. The original conveyance language controls.",
      "Sewer easements often run down the shared line between two lots.",
    ],
  },
  {
    slug: "salt-lake",
    county: "Salt Lake County",
    stateCode: "UT",
    stateName: "Utah",
    metro: "Salt Lake City",
    recordingOffice: "Salt Lake County Recorder",
    publicGis: true,
    intro:
      "Historic irrigation ditches run through many Salt Lake County neighborhoods, often with rights far older than the houses now sitting beside them.",
    commonEasements: [
      "Irrigation ditch and canal easements",
      "Public utility easements",
      "Shared driveway easements on older blocks",
      "Storm drain easements",
    ],
    recorderUrl: "https://recorder.slco.org/",
    feeNote:
      "Utah charges a flat fee for the first page of a real property document plus a per-page fee, with an extra charge for each additional name indexed.",
    localNotes: [
      "Ditch companies hold enforceable access rights even where the ditch is now piped.",
      "Some ditch easements are prescriptive rather than recorded.",
    ],
  },
  {
    slug: "denver",
    county: "Denver County",
    stateCode: "CO",
    stateName: "Colorado",
    metro: "Denver",
    recordingOffice: "Denver Clerk and Recorder",
    publicGis: true,
    intro:
      "Denver's alley grid and ditch history define its easements: nearly every older block has a public alley, and ditch rights survive in pockets across the city.",
    commonEasements: [
      "Public alley easements",
      "Historic ditch and lateral easements",
      "Utility easements for overhead lines in alleys",
      "Access easements for detached accessory units",
    ],
    recorderUrl: "https://denvergov.org/Government/Agencies-Departments-Offices/Agencies-Departments-Offices-Directory/Clerk-and-Recorder",
    feeNote:
      "Colorado's statutory recording fee is a first-page charge plus a per-page charge, and a small state surcharge applies to most documents.",
    localNotes: [
      "Building an ADU often turns on whether the alley easement supports access.",
      "Ditch laterals may be piped but still legally protected.",
    ],
  },
  {
    slug: "jefferson",
    county: "Jefferson County",
    stateCode: "CO",
    stateName: "Colorado",
    metro: "Golden",
    recordingOffice: "Jefferson County Clerk and Recorder",
    publicGis: true,
    intro:
      "Foothills parcels in Jefferson County frequently depend on private road easements, and mineral estates are often severed from the surface.",
    commonEasements: [
      "Private road and driveway easements in the foothills",
      "Utility easements to remote parcels",
      "Ditch and water conveyance easements",
      "Severed mineral estate access rights",
    ],
    recorderUrl: "https://www.jeffco.us/",
    feeNote:
      "Colorado charges a first-page fee plus a per-page fee under the statewide statutory schedule, plus a small surcharge on most instruments.",
    localNotes: [
      "A severed mineral estate can carry surface access rights unless expressly waived.",
      "Private road maintenance agreements matter as much as the easement grant.",
    ],
  },
  {
    slug: "hennepin",
    county: "Hennepin County",
    stateCode: "MN",
    stateName: "Minnesota",
    metro: "Minneapolis",
    recordingOffice: "Hennepin County Recorder / Registrar of Titles",
    publicGis: true,
    intro:
      "Hennepin County runs both abstract and Torrens title systems, which changes how easements are proven, a distinction that matters more here than almost anywhere else.",
    commonEasements: [
      "Public utility and alley easements",
      "Storm sewer and lake drainage easements",
      "Shared driveway easements",
      "Trail and park easements",
    ],
    recorderUrl: "https://www.hennepin.us/residents/property/recording-documents",
    feeNote:
      "Minnesota sets a flat per-document recording fee statewide, and abstract and Torrens property are filed separately, a Torrens easement also needs the certificate of title updated.",
    localNotes: [
      "Torrens property lists easements as memorials on the certificate of title.",
      "Prescriptive rights generally cannot be claimed against Torrens property.",
    ],
  },
  {
    slug: "suffolk",
    county: "Suffolk County",
    stateCode: "NY",
    stateName: "New York",
    metro: "Long Island",
    recordingOffice: "Suffolk County Clerk",
    publicGis: true,
    intro:
      "Long Island's mix of old rights-of-way, beach access, and modern utility work makes Suffolk County easements unusually varied for a suburban county.",
    commonEasements: [
      "Rights-of-way to the shoreline",
      "Utility easements for power and gas",
      "Private road easements in unincorporated hamlets",
      "Drainage and recharge basin easements",
    ],
    recorderUrl: "https://www.suffolkcountyny.gov/clerk",
    feeNote:
      "New York recording costs combine a per-page fee with a block-and-lot charge and a records management fee, so a long easement with exhibits adds up quickly.",
    localNotes: [
      "Shoreline access rights may be held by a community association rather than the public.",
      "Old rights-of-way can survive on paper long after the physical path disappears.",
    ],
  },
  {
    slug: "jefferson",
    county: "Jefferson County",
    stateCode: "AL",
    stateName: "Alabama",
    metro: "Birmingham",
    recordingOffice: "Jefferson County Probate Court, Recording Division",
    recorderUrl: "https://jeffcoprobatecourt.com/land-records/",
    publicGis: true,
    intro:
      "Alabama records land instruments through the Probate Judge rather than a county recorder, and Jefferson County splits filings between the Birmingham and Bessemer divisions, so the correct office depends on where the land sits.",
    commonEasements: [
      "Alabama Power distribution and transmission easements",
      "Sanitary sewer easements held by the county",
      "Mining and mineral access rights on former coal land",
      "Shared driveway easements on older Birmingham lots",
    ],
    localNotes: [
      "Confirm whether your parcel files in the Birmingham or Bessemer division before you send an instrument for recording.",
      "Historic mineral reservations are common here and are recorded separately from surface easements.",
    ],
    feeNote:
      "Alabama recording fees are set by statute per page, with a separate deed tax on transfers. The probate office publishes the current schedule.",
  },
  {
    slug: "anchorage",
    county: "Anchorage Recording District",
    stateCode: "AK",
    stateName: "Alaska",
    metro: "Anchorage",
    recordingOffice: "Alaska DNR Recorder's Office, Anchorage Recording District",
    recorderUrl: "https://dnr.alaska.gov/ssd/recoff/",
    publicGis: true,
    intro:
      "Alaska has no counties. The state Department of Natural Resources runs recording districts, and everything for the Anchorage bowl and Mat-Su corridor is filed and searched through the state system rather than a local office.",
    commonEasements: [
      "Section-line easements reserved across state and former federal land",
      "Utility easements held by Chugach Electric and local cooperatives",
      "Native corporation access and right-of-way reservations",
      "Snow storage and winter access easements on rural tracts",
    ],
    localNotes: [
      "Section-line easements are a statewide feature and frequently affect rural parcels even when no document names them.",
      "Search the recording district, not a county, when you order copies of an instrument.",
    ],
    feeNote:
      "Recording fees are set statewide by the Department of Natural Resources and are the same in every recording district.",
  },
  {
    slug: "pulaski",
    county: "Pulaski County",
    stateCode: "AR",
    stateName: "Arkansas",
    metro: "Little Rock",
    recordingOffice: "Pulaski County Circuit/County Clerk, Real Estate Division",
    recorderUrl: "https://www.pulaskiclerk.com/",
    publicGis: true,
    intro:
      "Arkansas records deeds and easements with the Circuit Clerk, and Pulaski County handles the heaviest volume in the state through its real estate division in Little Rock.",
    commonEasements: [
      "Entergy Arkansas distribution and transmission easements",
      "Drainage easements along tributaries of the Arkansas River",
      "Pipeline easements crossing the outer county",
      "Access easements serving unplatted rural tracts",
    ],
    localNotes: [
      "Floodplain and drainage easements are common near the river and can restrict fill as well as structures.",
      "Rural tracts split off larger farms frequently rely on an access easement described only by metes and bounds.",
    ],
    feeNote:
      "Arkansas sets recording fees by statute with a first-page rate and a lower per-page rate after that, plus a real property transfer tax on conveyances.",
  },
  {
    slug: "hartford",
    county: "Hartford",
    stateCode: "CT",
    stateName: "Connecticut",
    metro: "Hartford",
    recordingOffice: "Hartford Town and City Clerk, Land Records",
    recorderUrl: "https://www.hartfordct.gov/Government/Departments/Town-City-Clerk",
    publicGis: true,
    intro:
      "Connecticut has no county recording offices. Land records are kept town by town, so an easement affecting a Hartford parcel is filed with the Hartford Town Clerk and never appears in a county index.",
    commonEasements: [
      "Eversource electric and gas easements",
      "Municipal sewer and storm drainage easements",
      "Shared driveway and party wall rights on dense urban lots",
      "Conservation restrictions held by the town or a land trust",
    ],
    localNotes: [
      "Search the specific town's land records, not a county office, and remember a parcel near a boundary may have documents in two towns.",
      "Older Hartford lots frequently carry unwritten but long-standing shared access arrangements worth documenting.",
    ],
    feeNote:
      "Connecticut sets a statewide per-page recording fee by statute; conveyance taxes are collected separately by the town and the state.",
  },
  {
    slug: "new-castle",
    county: "New Castle County",
    stateCode: "DE",
    stateName: "Delaware",
    metro: "Wilmington",
    recordingOffice: "New Castle County Recorder of Deeds",
    recorderUrl: "https://www.newcastlede.gov/136/Recorder-of-Deeds",
    publicGis: true,
    intro:
      "New Castle County holds most of Delaware's population and its Recorder of Deeds indexes every easement affecting land from Wilmington south to the canal.",
    commonEasements: [
      "Delmarva Power distribution easements",
      "County sewer and stormwater management easements",
      "Drainage and tax ditch rights of way",
      "Access easements serving flag lots in unincorporated areas",
    ],
    localNotes: [
      "Tax ditch rights of way are a Delaware feature and can be far wider than the visible channel.",
      "Stormwater management facilities on residential lots usually come with a recorded maintenance easement.",
    ],
    feeNote:
      "Delaware recording fees are charged per document with additional county surcharges, and a realty transfer tax applies to conveyances.",
  },
  {
    slug: "district-of-columbia",
    county: "District of Columbia",
    stateCode: "DC",
    stateName: "District of Columbia",
    metro: "Washington",
    recordingOffice: "DC Office of Tax and Revenue, Recorder of Deeds",
    recorderUrl: "https://otr.cfo.dc.gov/page/recorder-deeds",
    publicGis: true,
    intro:
      "The District is a single recording jurisdiction. Every easement, covenant and deed for a DC square and lot is filed with the Recorder of Deeds inside the Office of Tax and Revenue.",
    commonEasements: [
      "Public space and vault easements under sidewalks",
      "Pepco and Washington Gas utility easements",
      "Historic preservation easements held by nonprofit holders",
      "Party wall and shared alley rights on rowhouse lots",
    ],
    localNotes: [
      "Rowhouse alleys are frequently private with recorded reciprocal rights rather than public streets.",
      "Preservation easements on historic facades restrict alterations permanently and are recorded against the lot.",
    ],
    feeNote:
      "The District charges a per-document recording fee and separate recordation and transfer taxes on conveyances.",
  },
  {
    slug: "honolulu",
    county: "Honolulu County",
    stateCode: "HI",
    stateName: "Hawaii",
    metro: "Honolulu",
    recordingOffice: "Hawaii Bureau of Conveyances (statewide)",
    recorderUrl: "https://dlnr.hawaii.gov/boc/",
    publicGis: true,
    intro:
      "Hawaii records every island's land documents in one statewide Bureau of Conveyances, which maintains both a regular system and a Land Court registered system, and which system your parcel sits in changes how an easement is filed.",
    commonEasements: [
      "Access easements serving kuleana parcels",
      "Hawaiian Electric and Board of Water Supply easements",
      "Shoreline and public beach access easements",
      "Drainage easements across steep valley lots",
    ],
    localNotes: [
      "Find out whether the parcel is Land Court registered or regular system before ordering documents; the indexes are separate.",
      "Kuleana access rights can burden surrounding land even where no modern easement was ever drafted.",
    ],
    feeNote:
      "The Bureau of Conveyances charges a flat per-document fee that differs between the regular system and Land Court, plus a conveyance tax on transfers.",
  },
  {
    slug: "ada",
    county: "Ada County",
    stateCode: "ID",
    stateName: "Idaho",
    metro: "Boise",
    recordingOffice: "Ada County Recorder",
    recorderUrl: "https://adacounty.id.gov/recorder/",
    publicGis: true,
    intro:
      "Growth around Boise has layered modern subdivision easements on top of a century of irrigation rights, and Ada County parcels frequently carry both.",
    commonEasements: [
      "Irrigation district ditch and lateral easements",
      "Idaho Power distribution and transmission easements",
      "Pressurized irrigation and drainage easements in newer subdivisions",
      "Shared access easements on county-road frontage",
    ],
    localNotes: [
      "Ditch and lateral rights include a right of access for maintenance that is often wider than the channel itself.",
      "Irrigation districts, not the county, control what may be built near a lateral.",
    ],
    feeNote:
      "Idaho sets a flat per-document recording fee by statute, with the same rate at every county recorder.",
  },
  {
    slug: "marion",
    county: "Marion County",
    stateCode: "IN",
    stateName: "Indiana",
    metro: "Indianapolis",
    recordingOffice: "Marion County Recorder",
    recorderUrl: "https://www.indy.gov/agency/marion-county-recorder",
    publicGis: true,
    intro:
      "Marion County covers Indianapolis, where combined sewer work, legal drains and utility corridors put recorded easements across a large share of residential lots.",
    commonEasements: [
      "Citizens Energy Group sewer and water easements",
      "Legal drain easements administered by the county surveyor",
      "AES Indiana distribution easements",
      "Platted utility and drainage easements along rear lot lines",
    ],
    localNotes: [
      "Regulated legal drains carry a statutory maintenance corridor on each side that is not always shown on the plat.",
      "The county surveyor's office is the place to confirm a legal drain, separately from the recorder.",
    ],
    feeNote:
      "Indiana recording fees are set by statute as a flat per-document charge that varies by document type rather than by page count.",
  },
  {
    slug: "polk",
    county: "Polk County",
    stateCode: "IA",
    stateName: "Iowa",
    metro: "Des Moines",
    recordingOffice: "Polk County Recorder",
    recorderUrl: "https://www.polkcountyiowa.gov/county-recorder/",
    publicGis: true,
    intro:
      "Polk County records for the Des Moines metro, where drainage districts and utility corridors dominate easement questions on both farm ground and new subdivisions.",
    commonEasements: [
      "Drainage district tile and open ditch easements",
      "MidAmerican Energy electric and gas easements",
      "Sanitary sewer easements held by cities and sewer districts",
      "Field access easements on split farm parcels",
    ],
    localNotes: [
      "Buried drainage tile can carry rights that no recorded document describes precisely; the drainage district holds the records.",
      "Farmland split for development often keeps an access easement for the remaining field.",
    ],
    feeNote:
      "Iowa recording fees are set by statute with a first-page rate and a per-page rate after that, plus a transfer tax on conveyances.",
  },
  {
    slug: "johnson",
    county: "Johnson County",
    stateCode: "KS",
    stateName: "Kansas",
    metro: "Overland Park",
    recordingOffice: "Johnson County Register of Deeds",
    recorderUrl: "https://www.jocogov.org/department/register-deeds",
    publicGis: true,
    intro:
      "Johnson County is the fastest-growing part of Kansas, and its subdivisions carry dense platted utility and stormwater easements alongside older pipeline corridors crossing the county.",
    commonEasements: [
      "Evergy electric distribution and transmission easements",
      "Stormwater detention and drainage easements",
      "Interstate pipeline corridors across the western county",
      "Sanitary sewer easements held by Johnson County Wastewater",
    ],
    localNotes: [
      "Detention basin easements can consume much of a rear yard on newer lots and prohibit fill.",
      "Pipeline corridors are usually surveyed and marked; the recorded width often exceeds the marked path.",
    ],
    feeNote:
      "Kansas registers of deeds charge a statutory first-page fee plus a per-page rate, with a technology fund surcharge.",
  },
  {
    slug: "jefferson",
    county: "Jefferson County",
    stateCode: "KY",
    stateName: "Kentucky",
    metro: "Louisville",
    recordingOffice: "Jefferson County Clerk, Land Records",
    recorderUrl: "https://www.jeffersoncountyclerk.org/",
    publicGis: true,
    intro:
      "Kentucky records land instruments with the County Clerk, and Jefferson County covers the Louisville metro, where combined sewer easements and old alley rights are the usual complications.",
    commonEasements: [
      "MSD sanitary and stormwater easements",
      "LG&E electric and gas easements",
      "Vacated alley and shared access rights on older city lots",
      "Floodplain and drainage easements near the Ohio River",
    ],
    localNotes: [
      "Alleys vacated by the city can leave private access rights in place for adjoining owners.",
      "MSD easements often include a right to enter for maintenance well beyond the pipe itself.",
    ],
    feeNote:
      "Kentucky sets clerk recording fees by statute as a flat fee covering a set number of pages, with a per-page charge beyond that.",
  },
  {
    slug: "east-baton-rouge",
    county: "East Baton Rouge Parish",
    stateCode: "LA",
    stateName: "Louisiana",
    metro: "Baton Rouge",
    recordingOffice: "East Baton Rouge Parish Clerk of Court",
    recorderUrl: "https://www.ebrclerkofcourt.org/",
    publicGis: true,
    intro:
      "Louisiana uses parishes and civil law, so what other states call an easement is a servitude here, recorded with the Clerk of Court and governed by the Civil Code rather than common law.",
    commonEasements: [
      "Predial servitudes of passage serving enclosed estates",
      "Entergy Louisiana utility servitudes",
      "Drainage servitudes along canals and coulees",
      "Pipeline servitudes crossing the parish",
    ],
    localNotes: [
      "Search under servitude as well as easement; the terminology in the index follows the Civil Code.",
      "Rights of passage for enclosed estates arise under the Code and may exist without a negotiated document.",
    ],
    feeNote:
      "Louisiana clerks of court set recording fees locally within statutory limits, typically a base fee plus a per-page charge.",
  },
  {
    slug: "cumberland",
    county: "Cumberland County",
    stateCode: "ME",
    stateName: "Maine",
    metro: "Portland",
    recordingOffice: "Cumberland County Registry of Deeds",
    recorderUrl: "https://www.cumberlandcounty.org/185/Registry-of-Deeds",
    publicGis: true,
    intro:
      "Maine records through county registries of deeds, and Cumberland County covers Portland and the surrounding coast, where old rights of way and shore access rights dominate easement work.",
    commonEasements: [
      "Deeded rights of way to great ponds and the shore",
      "Central Maine Power distribution easements",
      "Private road easements serving camp and coastal lots",
      "Conservation easements held by regional land trusts",
    ],
    localNotes: [
      "Paper rights of way described in nineteenth-century deeds are still enforceable and frequently unmarked on the ground.",
      "Private road associations govern maintenance on many coastal and lake roads.",
    ],
    feeNote:
      "Maine registries charge a statutory per-page recording fee, and a real estate transfer tax applies to conveyances.",
  },
  {
    slug: "montgomery",
    county: "Montgomery County",
    stateCode: "MD",
    stateName: "Maryland",
    metro: "Silver Spring",
    recordingOffice: "Montgomery County Circuit Court, Land Records Department",
    recorderUrl: "https://www.montgomerycountymd.gov/circuit-court/how-do-i/land-records",
    publicGis: true,
    intro:
      "Maryland records land documents through the Circuit Court clerk in each county, and Montgomery County adds a step: most instruments pass through the county's transfer and recordation review before the clerk will accept them.",
    commonEasements: [
      "WSSC water and sewer easements",
      "Pepco transmission and distribution corridors",
      "Stormwater management and forest conservation easements",
      "Historic and agricultural preservation easements",
    ],
    localNotes: [
      "Forest conservation easements are recorded against the lot and permanently restrict clearing in the protected area.",
      "Documents generally need county transfer office review before the clerk records them.",
    ],
    feeNote:
      "Maryland charges a statutory clerk recording fee per instrument plus state recordation tax and county transfer tax on conveyances.",
  },
  {
    slug: "middlesex",
    county: "Middlesex County",
    stateCode: "MA",
    stateName: "Massachusetts",
    metro: "Cambridge",
    recordingOffice: "Middlesex South Registry of Deeds",
    recorderUrl: "https://www.middlesexsouthregistry.com/",
    publicGis: true,
    intro:
      "Massachusetts registries are split by district, and Middlesex has both a South and a North registry. Each also runs a separate Land Court registered land section, so the same county holds two parallel systems.",
    commonEasements: [
      "Registered land easements noted on Land Court certificates",
      "Eversource electric and gas easements",
      "Municipal sewer and drain easements",
      "Conservation restrictions approved by the state and held locally",
    ],
    localNotes: [
      "Check whether the parcel is recorded or registered land; easements on registered land must appear on the certificate of title.",
      "Conservation restrictions in Massachusetts require state approval and are permanent once recorded.",
    ],
    feeNote:
      "Massachusetts sets registry recording fees by statute at a flat rate per document type, plus deeds excise on conveyances.",
  },
  {
    slug: "wayne",
    county: "Wayne County",
    stateCode: "MI",
    stateName: "Michigan",
    metro: "Detroit",
    recordingOffice: "Wayne County Register of Deeds",
    recorderUrl: "https://www.waynecounty.com/elected/deeds/home.aspx",
    publicGis: true,
    intro:
      "Wayne County records for Detroit and its inner suburbs, where narrow platted lots, rear public alleys and old utility corridors generate most easement questions.",
    commonEasements: [
      "Platted rear alley and utility easements",
      "DTE Energy electric and gas easements",
      "Great Lakes Water Authority and municipal sewer easements",
      "Drain easements administered by the county",
    ],
    localNotes: [
      "County drains carry statutory maintenance corridors that are separate from platted easements.",
      "Vacated alleys often leave utility rights in place even after the public right of way ends.",
    ],
    feeNote:
      "Michigan sets a flat statutory per-document recording fee statewide, plus state and county real estate transfer taxes on conveyances.",
  },
  {
    slug: "hinds",
    county: "Hinds County",
    stateCode: "MS",
    stateName: "Mississippi",
    metro: "Jackson",
    recordingOffice: "Hinds County Chancery Clerk",
    recorderUrl: "https://www.co.hinds.ms.us/pgs/chancery.asp",
    publicGis: true,
    intro:
      "Mississippi records land instruments with the Chancery Clerk, and Hinds County splits filings between judicial districts in Jackson and Raymond depending on where the property lies.",
    commonEasements: [
      "Entergy Mississippi distribution easements",
      "Sanitary sewer easements held by the city of Jackson",
      "Drainage easements along creeks and detention areas",
      "Timber and field access easements on rural tracts",
    ],
    localNotes: [
      "Confirm the judicial district before filing; the wrong district means the instrument does not appear in the right index.",
      "Rural access easements are frequently described by reference to old field lines rather than a survey.",
    ],
    feeNote:
      "Mississippi chancery clerks charge statutory recording fees per instrument, with additional per-page charges for longer documents.",
  },
  {
    slug: "st-louis-county",
    county: "St. Louis County",
    stateCode: "MO",
    stateName: "Missouri",
    metro: "St. Louis",
    recordingOffice: "St. Louis County Recorder of Deeds",
    recorderUrl: "https://stlouiscountymo.gov/st-louis-county-departments/recorder-of-deeds/",
    publicGis: true,
    intro:
      "St. Louis County is a separate jurisdiction from the City of St. Louis, and each keeps its own land records, so the first question on any easement search here is which of the two holds the document.",
    commonEasements: [
      "MSD sanitary and stormwater easements",
      "Ameren Missouri electric and gas easements",
      "Subdivision common ground and drainage easements",
      "Shared driveway easements on older inner-ring lots",
    ],
    localNotes: [
      "City of St. Louis parcels are recorded with the city, not the county; the two indexes do not overlap.",
      "Subdivision trustees frequently hold rights over common ground that function like easements on adjoining lots.",
    ],
    feeNote:
      "Missouri sets recorder fees by statute with a first-page rate and a lower per-page rate after that.",
  },
  {
    slug: "yellowstone",
    county: "Yellowstone County",
    stateCode: "MT",
    stateName: "Montana",
    metro: "Billings",
    recordingOffice: "Yellowstone County Clerk and Recorder",
    recorderUrl: "https://www.yellowstonecountymt.gov/Clerk/",
    publicGis: true,
    intro:
      "Yellowstone County covers Billings and a wide band of agricultural land, where irrigation ditches, section-line access and utility corridors are the recurring easement issues.",
    commonEasements: [
      "Irrigation ditch and headgate easements",
      "NorthWestern Energy distribution easements",
      "Pipeline and refinery corridor easements",
      "Access easements across large agricultural tracts",
    ],
    localNotes: [
      "Montana law protects existing ditch rights, including access to maintain them, even against later purchasers.",
      "Rural access easements often follow section lines and predate any modern survey.",
    ],
    feeNote:
      "Montana clerk and recorder fees are set by statute at a per-page rate that is uniform across counties.",
  },
  {
    slug: "douglas",
    county: "Douglas County",
    stateCode: "NE",
    stateName: "Nebraska",
    metro: "Omaha",
    recordingOffice: "Douglas County Register of Deeds",
    recorderUrl: "https://www.douglascounty-ne.gov/register-of-deeds",
    publicGis: true,
    intro:
      "Douglas County records for Omaha, where combined sewer separation work and rapid western expansion have both added recorded easements to residential lots.",
    commonEasements: [
      "OPPD electric distribution and transmission easements",
      "Sanitary and storm sewer easements held by the city",
      "Platted utility easements along subdivision lot lines",
      "Drainage and detention easements in newer developments",
    ],
    localNotes: [
      "Sewer separation projects have added new recorded easements on older lots within the last two decades.",
      "Subdivision plats in west Omaha routinely place a utility easement along every lot line.",
    ],
    feeNote:
      "Nebraska sets register of deeds fees by statute with a first-page rate and a per-page rate after that, plus documentary stamp tax on conveyances.",
  },
  {
    slug: "hillsborough",
    county: "Hillsborough County",
    stateCode: "NH",
    stateName: "New Hampshire",
    metro: "Manchester",
    recordingOffice: "Hillsborough County Registry of Deeds",
    recorderUrl: "https://www.nhdeeds.org/",
    publicGis: true,
    intro:
      "New Hampshire records at the county registry, and Hillsborough County covers Manchester and Nashua, where private roads and old rights of way over former farmland are the common easement issues.",
    commonEasements: [
      "Private road and shared driveway easements",
      "Eversource electric transmission corridors",
      "Conservation easements held by town conservation commissions",
      "Drainage and wetland buffer easements",
    ],
    localNotes: [
      "Class VI roads are public in name but unmaintained; access frequently depends on a private agreement.",
      "Town conservation commissions hold restrictions that never appear in a utility easement search.",
    ],
    feeNote:
      "New Hampshire registries charge a statutory per-page recording fee, with a separate real estate transfer tax on conveyances.",
  },
  {
    slug: "bergen",
    county: "Bergen County",
    stateCode: "NJ",
    stateName: "New Jersey",
    metro: "Hackensack",
    recordingOffice: "Bergen County Clerk, Land Records",
    recorderUrl: "https://www.co.bergen.nj.us/county-clerk",
    publicGis: true,
    intro:
      "Bergen County has more municipalities than any other in New Jersey, so zoning and setback rules change block to block even though every easement is recorded in one county index.",
    commonEasements: [
      "PSE&G electric and gas easements",
      "Municipal sewer and drainage easements",
      "Shared driveway easements on narrow suburban lots",
      "Flood hazard and stream corridor easements",
    ],
    localNotes: [
      "Easements are county-recorded but setbacks are municipal; both must be checked before siting a structure.",
      "Stream corridor restrictions under state flood rules can bind land that carries no recorded easement at all.",
    ],
    feeNote:
      "New Jersey sets county clerk recording fees by statute with a first-page rate plus a per-page rate, and realty transfer fees on conveyances.",
  },
  {
    slug: "bernalillo",
    county: "Bernalillo County",
    stateCode: "NM",
    stateName: "New Mexico",
    metro: "Albuquerque",
    recordingOffice: "Bernalillo County Clerk",
    recorderUrl: "https://www.bernco.gov/clerk/",
    publicGis: true,
    intro:
      "Bernalillo County covers Albuquerque, where irrigation ditches maintained by a conservancy district and Spanish and Mexican land grant boundaries both shape modern easement questions.",
    commonEasements: [
      "MRGCD acequia and drainage ditch easements",
      "PNM electric distribution easements",
      "Access easements serving unplatted land grant tracts",
      "Arroyo and flood control easements",
    ],
    localNotes: [
      "Acequia and conservancy district ditches carry maintenance access rights that limit fencing and planting alongside them.",
      "Land grant history means some access rights are far older than the county's recorded index.",
    ],
    feeNote:
      "New Mexico county clerks charge a flat statutory recording fee per document regardless of page count.",
  },
  {
    slug: "cass",
    county: "Cass County",
    stateCode: "ND",
    stateName: "North Dakota",
    metro: "Fargo",
    recordingOffice: "Cass County Recorder",
    recorderUrl: "https://www.casscountynd.gov/government/departments/recorder",
    publicGis: true,
    intro:
      "Cass County covers Fargo and the flat Red River Valley, where flood protection and agricultural drainage produce most of the recorded easements.",
    commonEasements: [
      "Flood protection and levee easements",
      "Legal drain and assessment district easements",
      "Xcel Energy and cooperative electric easements",
      "Section-line access across farmland",
    ],
    localNotes: [
      "Flood buyout and levee projects have added permanent easements along the river within living memory.",
      "Section-line rights of way exist by statute across most rural land whether or not a deed mentions them.",
    ],
    feeNote:
      "North Dakota sets recorder fees by statute at a per-page rate applied uniformly across counties.",
  },
  {
    slug: "oklahoma",
    county: "Oklahoma County",
    stateCode: "OK",
    stateName: "Oklahoma",
    metro: "Oklahoma City",
    recordingOffice: "Oklahoma County Clerk",
    recorderUrl: "https://oklahomacounty.org/county-clerk",
    publicGis: true,
    intro:
      "Oklahoma County records for Oklahoma City, where severed mineral ownership means surface owners frequently deal with access rights held by someone who owns nothing visible on the land.",
    commonEasements: [
      "Oil and gas surface access and pipeline easements",
      "OG&E electric distribution and transmission easements",
      "Drainage and detention easements in newer additions",
      "Section-line road easements outside the city",
    ],
    localNotes: [
      "Severed minerals carry an implied right of reasonable surface use; a surface-only purchase does not remove it.",
      "Pipeline corridors crossing suburban additions are often older than the subdivision itself.",
    ],
    feeNote:
      "Oklahoma county clerks charge statutory recording fees with a first-page rate and a per-page rate after that, plus documentary stamps on deeds.",
  },
  {
    slug: "multnomah",
    county: "Multnomah County",
    stateCode: "OR",
    stateName: "Oregon",
    metro: "Portland",
    recordingOffice: "Multnomah County Recording, Division of Assessment and Taxation",
    recorderUrl: "https://www.multco.us/recording",
    publicGis: true,
    intro:
      "Multnomah County records for Portland, where flag lots, steep west hills and stormwater rules combine to put access and drainage easements on a large share of infill parcels.",
    commonEasements: [
      "Shared access easements serving flag lots",
      "PGE and NW Natural utility easements",
      "Stormwater and drainage easements on hillside lots",
      "Tree preservation and view easements in some neighborhoods",
    ],
    localNotes: [
      "Infill partitions commonly create a shared driveway easement with a recorded maintenance agreement.",
      "Hillside lots frequently carry both a slope stability restriction and a drainage easement.",
    ],
    feeNote:
      "Oregon sets recording fees by statute with a first-page rate plus a per-page rate, and adds a statewide housing document surcharge.",
  },
  {
    slug: "philadelphia",
    county: "Philadelphia County",
    stateCode: "PA",
    stateName: "Pennsylvania",
    metro: "Philadelphia",
    recordingOffice: "Philadelphia Department of Records",
    recorderUrl: "https://www.phila.gov/departments/department-of-records/",
    publicGis: true,
    intro:
      "Philadelphia is both a city and a county, and its Department of Records handles recording for the whole jurisdiction, where rowhouse party walls and private alleys generate most easement disputes.",
    commonEasements: [
      "Party wall rights between adjoining rowhouses",
      "Private alley and courtyard access rights",
      "PECO and Philadelphia Water Department easements",
      "Sidewalk vault and encroachment agreements",
    ],
    localNotes: [
      "Party wall rights are frequently established by long use and old deeds rather than a standalone easement document.",
      "Private alleys behind rowhouse blocks are often owned in common by the abutting owners.",
    ],
    feeNote:
      "Philadelphia charges a per-document recording fee plus city and state realty transfer taxes on conveyances.",
  },
  {
    slug: "providence",
    county: "Providence",
    stateCode: "RI",
    stateName: "Rhode Island",
    metro: "Providence",
    recordingOffice: "City of Providence Recorder of Deeds",
    recorderUrl: "https://www.providenceri.gov/recorder-of-deeds/",
    publicGis: true,
    intro:
      "Rhode Island records at the city and town level rather than the county level, so an easement affecting a Providence parcel is filed with the city, and a parcel one street over in another town is not in the same index.",
    commonEasements: [
      "Rhode Island Energy electric and gas easements",
      "Municipal sewer and drainage easements",
      "Shared driveway and party wall rights on dense city lots",
      "Historic preservation easements downtown",
    ],
    localNotes: [
      "There is no county index; search the specific municipality where the land sits.",
      "Historic district restrictions can be recorded separately from any preservation easement.",
    ],
    feeNote:
      "Rhode Island sets municipal recording fees by statute with a base document fee and per-page charges beyond a set length.",
  },
  {
    slug: "greenville",
    county: "Greenville County",
    stateCode: "SC",
    stateName: "South Carolina",
    metro: "Greenville",
    recordingOffice: "Greenville County Register of Deeds",
    recorderUrl: "https://www.greenvillecounty.org/RegisterOfDeeds/",
    publicGis: true,
    intro:
      "Greenville County is the largest in South Carolina and its upstate topography means drainage, slope and shared access easements appear on a large share of residential title work.",
    commonEasements: [
      "Duke Energy distribution and transmission easements",
      "Stormwater and detention pond easements",
      "Shared driveway easements on subdivided rural tracts",
      "Sewer easements held by Renewable Water Resources",
    ],
    localNotes: [
      "Detention pond easements on subdivision lots come with maintenance duties written into the covenants.",
      "Rural tracts split over the years often depend on an access easement that was never surveyed.",
    ],
    feeNote:
      "South Carolina sets a flat statutory recording fee per document type, plus deed recording fees based on the sale price.",
  },
  {
    slug: "minnehaha",
    county: "Minnehaha County",
    stateCode: "SD",
    stateName: "South Dakota",
    metro: "Sioux Falls",
    recordingOffice: "Minnehaha County Register of Deeds",
    recorderUrl: "https://www.minnehahacounty.gov/dept/rd/rd.php",
    publicGis: true,
    intro:
      "Minnehaha County records for Sioux Falls, where farmland is being platted quickly and new subdivision easements sit on top of long-standing drainage and section-line rights.",
    commonEasements: [
      "Drainage easements across former farm ground",
      "Xcel Energy and cooperative electric easements",
      "Section-line highway rights of way",
      "Platted utility easements in new additions",
    ],
    localNotes: [
      "Section-line rights of way exist by statute and can affect rural parcels with no recorded easement document.",
      "Newly platted additions typically dedicate drainage easements that limit grading as well as building.",
    ],
    feeNote:
      "South Dakota registers of deeds charge a statutory per-page recording fee that is the same in every county.",
  },
  {
    slug: "shelby",
    county: "Shelby County",
    stateCode: "TN",
    stateName: "Tennessee",
    metro: "Memphis",
    recordingOffice: "Shelby County Register of Deeds",
    recorderUrl: "https://register.shelby.tn.us/",
    publicGis: true,
    intro:
      "Shelby County records for Memphis, where utility district easements and drainage rights along the many bayous and ditches are the usual complications on residential title work.",
    commonEasements: [
      "MLGW electric, gas and water easements",
      "Drainage easements along bayous and improved ditches",
      "Sanitary sewer easements held by the city and county",
      "Shared access easements on unincorporated tracts",
    ],
    localNotes: [
      "Drainage easements along improved ditches frequently include a wide maintenance strip on both sides.",
      "Unincorporated county parcels may rely on a private access easement rather than a public road.",
    ],
    feeNote:
      "Tennessee sets register of deeds fees by statute per page, and a separate recordation tax applies to conveyances and to indebtedness.",
  },
  {
    slug: "burlington",
    county: "Chittenden County",
    stateCode: "VT",
    stateName: "Vermont",
    metro: "Burlington",
    recordingOffice: "City of Burlington Clerk and Treasurer, Land Records",
    recorderUrl: "https://www.burlingtonvt.gov/CT",
    publicGis: true,
    intro:
      "Vermont keeps land records at the town and city level, so an easement affecting a Burlington parcel is recorded with the city clerk and never appears in a Chittenden County index.",
    commonEasements: [
      "Green Mountain Power distribution easements",
      "Municipal sewer, water and stormwater easements",
      "Conservation easements held by town and statewide land trusts",
      "Trail and lakeshore access easements",
    ],
    localNotes: [
      "Search the specific town clerk's records; there is no county land records office in Vermont.",
      "Recreation trail easements across private land are common and are usually recorded and permanent.",
    ],
    feeNote:
      "Vermont sets a statewide per-page recording fee for town clerks, with a property transfer tax on conveyances.",
  },
  {
    slug: "fairfax",
    county: "Fairfax County",
    stateCode: "VA",
    stateName: "Virginia",
    metro: "Fairfax",
    recordingOffice: "Fairfax County Circuit Court, Land Records Division",
    recorderUrl: "https://www.fairfaxcounty.gov/circuit/land-records/general-information",
    publicGis: true,
    intro:
      "Virginia records land documents with the Circuit Court clerk, and Fairfax County handles one of the largest volumes in the country, with stormwater and resource protection easements alongside utility corridors.",
    commonEasements: [
      "Dominion Energy transmission and distribution easements",
      "Stormwater management and best management practice easements",
      "Resource protection area restrictions near streams",
      "Sanitary sewer easements held by the county",
    ],
    localNotes: [
      "Resource protection areas restrict clearing near streams even where no separate easement is recorded.",
      "Stormwater facilities on residential lots usually carry a recorded maintenance agreement with county access rights.",
    ],
    feeNote:
      "Virginia sets clerk recording fees by statute per instrument with page-count add-ons, plus state and local recordation taxes.",
  },
  {
    slug: "kanawha",
    county: "Kanawha County",
    stateCode: "WV",
    stateName: "West Virginia",
    metro: "Charleston",
    recordingOffice: "Kanawha County Clerk",
    recorderUrl: "https://kanawha.us/county-clerk/",
    publicGis: true,
    intro:
      "Kanawha County records for Charleston, where severed coal, oil and gas ownership means many surface owners share their land with mineral access rights recorded generations ago.",
    commonEasements: [
      "Oil, gas and coal surface access rights",
      "Gathering line and pipeline easements",
      "Appalachian Power distribution and transmission easements",
      "Hollow road access easements serving hillside parcels",
    ],
    localNotes: [
      "A severed mineral estate usually carries an implied right to use the surface reasonably; check the severance deed, not just recent instruments.",
      "Steep hollow parcels often depend on a shared private road with no written maintenance terms.",
    ],
    feeNote:
      "West Virginia county clerks charge statutory recording fees per instrument with page-count add-ons, plus excise tax on transfers.",
  },
  {
    slug: "milwaukee",
    county: "Milwaukee County",
    stateCode: "WI",
    stateName: "Wisconsin",
    metro: "Milwaukee",
    recordingOffice: "Milwaukee County Register of Deeds",
    recorderUrl: "https://county.milwaukee.gov/EN/Register-of-Deeds",
    publicGis: true,
    intro:
      "Milwaukee County records for the city and its inner suburbs, where narrow platted lots, shared driveways and sewer district easements produce most easement questions.",
    commonEasements: [
      "MMSD and municipal sanitary sewer easements",
      "We Energies electric and gas easements",
      "Shared driveway easements between narrow city lots",
      "Drainage easements along creeks and the river corridor",
    ],
    localNotes: [
      "Shared driveways on bungalow-era blocks often rely on a recorded reciprocal easement that both owners must honor.",
      "Sewer easements can run down the middle of a rear yard with no visible surface feature.",
    ],
    feeNote:
      "Wisconsin sets a flat statutory per-document recording fee statewide, plus a real estate transfer fee on conveyances.",
  },
  {
    slug: "laramie",
    county: "Laramie County",
    stateCode: "WY",
    stateName: "Wyoming",
    metro: "Cheyenne",
    recordingOffice: "Laramie County Clerk",
    recorderUrl: "https://www.laramiecountyclerk.com/",
    publicGis: true,
    intro:
      "Laramie County covers Cheyenne and a wide stretch of high plains, where wind development, transmission lines and irrigation rights are the recurring easement issues.",
    commonEasements: [
      "Wind energy lease and transmission easements",
      "Irrigation ditch and reservoir easements",
      "Black Hills Energy and rural electric cooperative easements",
      "Access easements across large ranch parcels",
    ],
    localNotes: [
      "Wind agreements often include wide access and collection line easements that outlast the original lease term.",
      "Ditch rights include access for maintenance and are protected against later purchasers.",
    ],
    feeNote:
      "Wyoming county clerks charge a statutory per-page recording fee that is uniform statewide.",
  },
  {
    slug: "kings",
    county: "Kings County",
    stateCode: "NY",
    stateName: "New York",
    metro: "Brooklyn",
    recordingOffice: "New York City Register (ACRIS)",
    publicGis: true,
    intro:
      "Brooklyn title work is dominated by party walls, shared alleys behind row houses and rooftop and vault rights, because lots are narrow, attached and were carved out long before modern zoning.",
    commonEasements: [
      "Party wall and shared foundation agreements between attached row houses",
      "Rear alley and common driveway easements serving a whole block face",
      "Light and air easements negotiated with neighboring developments",
      "Vault rights and sidewalk encroachment agreements under the public way",
    ],
    recorderUrl: "https://www.nyc.gov/site/finance/taxes/acris.page",
    feeNote:
      "Recording in the five boroughs runs through the City Register rather than a county clerk, with a per-document fee plus per-page charges and separate transfer tax filings handled in the same submission.",
    localNotes: [
      "Party wall rights in Brooklyn are frequently unwritten and rest on decades of shared use rather than a recorded grant.",
      "Vault space under the sidewalk is licensed by the city, not owned, and that obligation follows the building.",
    ],
  },
  {
    slug: "queens",
    county: "Queens County",
    stateCode: "NY",
    stateName: "New York",
    metro: "Queens",
    recordingOffice: "New York City Register for Queens (ACRIS)",
    publicGis: true,
    intro:
      "Queens mixes detached houses on platted blocks with dense corridors along the subway lines, so the recurring issues are shared driveways between semi-detached pairs and drainage across low-lying lots near Jamaica Bay.",
    commonEasements: [
      "Mutual driveway easements shared by semi-detached pairs",
      "Sewer and storm drainage easements across rear yards",
      "Utility service easements for buried electric and gas laterals",
      "Access easements to interior lots with no street frontage",
    ],
    recorderUrl: "https://www.nyc.gov/site/finance/taxes/acris.page",
    feeNote:
      "Queens instruments are filed through the City Register system, which charges per document and per page and collects the state and city transfer filings alongside the deed or grant.",
    localNotes: [
      "Mutual driveway rights are often written into 1920s deeds and never restated, so the original subdivision deed is the controlling document.",
      "Low elevation near the bay means drainage easements carry real maintenance duties, not just a right of passage for water.",
    ],
  },
  {
    slug: "new-york",
    county: "New York County",
    stateCode: "NY",
    stateName: "New York",
    metro: "Manhattan",
    recordingOffice: "New York City Register for New York County (ACRIS)",
    publicGis: true,
    intro:
      "Manhattan easement questions are rarely about a strip of ground. They are about air rights, light and air agreements, shared building systems and access for facade work on lot lines that touch on every side.",
    commonEasements: [
      "Development rights transfers and zoning lot merger agreements",
      "Light, air and view easements between adjacent towers",
      "Access easements for facade repair and scaffolding on a neighbor's roof",
      "Building systems easements for shared risers, vaults and conduits",
    ],
    recorderUrl: "https://www.nyc.gov/site/finance/taxes/acris.page",
    feeNote:
      "Manhattan filings go to the City Register, where a document fee plus page charges applies and zoning lot merger documents are recorded as a package rather than as a single grant.",
    localNotes: [
      "A zoning lot merger can permanently strip a building of unused development rights, and it is recorded like any other instrument.",
      "Access rights for facade inspection cycles are commonly granted by license rather than easement, which means they can end.",
    ],
  },
  {
    slug: "westchester",
    county: "Westchester County",
    stateCode: "NY",
    stateName: "New York",
    metro: "White Plains",
    recordingOffice: "Westchester County Clerk",
    publicGis: true,
    intro:
      "Westchester combines old estate subdivisions with reservoir and aqueduct land, so private road maintenance agreements and watershed conservation restrictions surface constantly in suburban title work.",
    commonEasements: [
      "Private road and shared driveway maintenance agreements",
      "New York City watershed and aqueduct easements",
      "Conservation restrictions on steep slopes and wetlands buffers",
      "Overhead electric distribution easements along older rights of way",
    ],
    recorderUrl: "https://www.westchesterclerk.com/",
    feeNote:
      "The County Clerk charges a recording fee per instrument plus a per-page charge, and cover pages generated by the office are counted in the page total.",
    localNotes: [
      "Aqueduct corridors run under residential neighborhoods and restrict digging and structures well beyond the visible right of way.",
      "Estate-era private roads often have no maintenance agreement at all, which becomes a lender issue at closing.",
    ],
  },
  {
    slug: "san-bernardino",
    county: "San Bernardino County",
    stateCode: "CA",
    stateName: "California",
    metro: "San Bernardino",
    recordingOffice: "San Bernardino County Assessor-Recorder-Clerk",
    publicGis: true,
    intro:
      "The largest county in the country by area runs from dense Inland Empire subdivisions out to open desert, so a single title chain can carry tract utility strips, flood control rights and mining or grazing access across unpatented ground.",
    commonEasements: [
      "Flood control and debris basin easements below the mountain front",
      "Southern California Edison transmission and distribution corridors",
      "Desert access and section line easements on remote parcels",
      "Water district pipeline and well site easements",
    ],
    recorderUrl: "https://arc.sbcounty.gov/",
    feeNote:
      "California recording is charged per first page plus each additional page, with extra fees for documents that do not meet formatting standards and for state housing surcharges.",
    localNotes: [
      "Remote desert parcels are often served only by prescriptive or historic section line access, not a recorded grant.",
      "Debris basin and wash easements can swallow the buildable portion of a lot at the base of the San Bernardino Mountains.",
    ],
  },
  {
    slug: "santa-clara",
    county: "Santa Clara County",
    stateCode: "CA",
    stateName: "California",
    metro: "San Jose",
    recordingOffice: "Santa Clara County Clerk-Recorder",
    publicGis: true,
    intro:
      "Silicon Valley infill puts pressure on narrow lots, so the easements that matter here are shared driveways on flag lots, creek and percolation pond rights, and utility undergrounding strips along frontages.",
    commonEasements: [
      "Flag lot access and shared driveway easements",
      "Water district creek, levee and percolation pond easements",
      "Undergrounding and joint trench utility easements at the front setback",
      "Public trail and open space easements along creek corridors",
    ],
    recorderUrl: "https://clerkrecorder.sccgov.org/",
    feeNote:
      "Fees follow the California per page structure, and the Clerk-Recorder adds the statewide affordable housing surcharge to most non-sale instruments.",
    localNotes: [
      "Creek corridor easements held by the water district often prohibit fences and structures inside the top-of-bank line.",
      "Second unit projects frequently fail when the shared driveway easement is too narrow for fire access.",
    ],
  },
  {
    slug: "sacramento",
    county: "Sacramento County",
    stateCode: "CA",
    stateName: "California",
    metro: "Sacramento",
    recordingOffice: "Sacramento County Clerk-Recorder",
    publicGis: true,
    intro:
      "Sacramento sits behind an extensive levee system on former floodplain, so levee maintenance rights, reclamation district canals and irrigation ditches show up in title far more often than in coastal counties.",
    commonEasements: [
      "Levee and reclamation district maintenance easements",
      "Irrigation and drainage ditch easements on former farmland",
      "SMUD electric distribution and streetlight easements",
      "Subdivision public utility easements along rear lot lines",
    ],
    recorderUrl: "https://ccr.saccounty.gov/",
    feeNote:
      "California charges by the page, and the Clerk-Recorder collects additional state surcharges on instruments that are not part of a residential sale.",
    localNotes: [
      "Reclamation district rights can include access to the rear of a lot for heavy equipment during high water.",
      "Ditch easements on converted farmland survive the subdivision even when the ditch itself was filled decades ago.",
    ],
  },
  {
    slug: "contra-costa",
    county: "Contra Costa County",
    stateCode: "CA",
    stateName: "California",
    metro: "Concord",
    recordingOffice: "Contra Costa County Clerk-Recorder",
    publicGis: true,
    intro:
      "Hillside subdivisions, refinery pipeline corridors and East Bay open space districts give Contra Costa an unusual mix of slope, geologic hazard and pipeline easements on ordinary residential lots.",
    commonEasements: [
      "Petroleum and gas pipeline corridors crossing residential tracts",
      "Slope, landslide repair and geologic hazard abatement easements",
      "Open space and scenic easements held by park districts",
      "Storm drain outfall easements on downhill lots",
    ],
    recorderUrl: "https://www.contracosta.ca.gov/191/Clerk-Recorder",
    feeNote:
      "Recording is priced per page under California law, with additional charges for nonconforming formatting and for state surcharges on non-sale documents.",
    localNotes: [
      "Pipeline operators hold wide corridors and enforce no-build and no-deep-rooting rules inside them.",
      "Geologic hazard abatement district assessments run with the land and often pair with a recorded slope easement.",
    ],
  },
  {
    slug: "san-mateo",
    county: "San Mateo County",
    stateCode: "CA",
    stateName: "California",
    metro: "Redwood City",
    recordingOffice: "San Mateo County Assessor-County Clerk-Recorder",
    publicGis: true,
    intro:
      "From bayfront levees to coastside ridges, San Mateo title work turns on shared access on steep private lanes, coastal trail dedications and sewer laterals crossing neighboring lots.",
    commonEasements: [
      "Private lane and hillside access easements",
      "Sanitary sewer lateral easements crossing an adjoining lot",
      "California Coastal Trail and public access dedications",
      "Bayfront levee and tidal flood protection easements",
    ],
    recorderUrl: "https://www.smcacre.gov/",
    feeNote:
      "The office charges the standard California first page and additional page fees and publishes formatting requirements that must be met before a document is accepted.",
    localNotes: [
      "Coastside parcels may carry an offer of dedication for public access that was accepted years after the subdivision.",
      "Hillside lanes are often maintained by informal agreement, and lenders increasingly require a recorded one.",
    ],
  },
  {
    slug: "tarrant",
    county: "Tarrant County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Fort Worth",
    recordingOffice: "Tarrant County Clerk",
    publicGis: true,
    intro:
      "Fort Worth sits over the Barnett Shale, so Tarrant title chains routinely carry pipeline and gas gathering easements alongside the usual platted utility and drainage strips.",
    commonEasements: [
      "Natural gas gathering line and pipeline easements",
      "Oncor electric distribution and transmission easements",
      "Platted utility and drainage easements along lot lines",
      "Sidewalk and street dedication easements in older additions",
    ],
    recorderUrl: "https://www.tarrantcountytx.gov/en/county-clerk.html",
    feeNote:
      "Texas clerks price recording at a first page rate plus a lower per additional page rate, so surveyed exhibits add to the cost of a filing.",
    localNotes: [
      "Gas pipeline easements frequently sit beneath back yards in subdivisions platted during the drilling boom.",
      "Mineral severance is common here, and surface access rights can accompany a mineral reservation even without a separate easement.",
    ],
  },
  {
    slug: "bexar",
    county: "Bexar County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "San Antonio",
    recordingOffice: "Bexar County Clerk",
    publicGis: true,
    intro:
      "San Antonio grows over the Edwards Aquifer recharge zone, which layers water quality restrictions and impervious cover limits on top of ordinary utility and drainage easements.",
    commonEasements: [
      "Edwards Aquifer recharge and water quality buffer easements",
      "CPS Energy electric and gas distribution easements",
      "Drainage and detention easements in newer subdivisions",
      "Military installation clear zone and avigation easements",
    ],
    recorderUrl: "https://www.bexar.org/1749/County-Clerk",
    feeNote:
      "The County Clerk charges a first page fee and a smaller fee for each additional page, and rejects documents that fail the statutory margin requirements.",
    localNotes: [
      "Avigation easements near the bases limit building height and waive noise claims permanently.",
      "Recharge zone restrictions can cap impervious cover across a whole tract, not just inside a marked easement.",
    ],
  },
  {
    slug: "collin",
    county: "Collin County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Plano",
    recordingOffice: "Collin County Clerk",
    publicGis: true,
    intro:
      "Collin County converted farmland into master planned communities at speed, so nearly every lot carries platted easements from the original development plan plus retained drainage courses from the fields that came before.",
    commonEasements: [
      "Master planned community utility and landscape easements",
      "Floodway and creek drainage easements retained from farmland",
      "Wall maintenance and screening easements along arterial roads",
      "Homeowners association common area access easements",
    ],
    recorderUrl: "https://www.collincountytx.gov/countyclerk",
    feeNote:
      "Filing costs follow the Texas first page plus per page model, and plats are charged on a separate schedule from ordinary instruments.",
    localNotes: [
      "Screening wall easements put maintenance duties on the lot owner even though the wall serves the whole subdivision.",
      "Drainage courses kept from the original farm often cross rear yards diagonally rather than along a lot line.",
    ],
  },
  {
    slug: "denton",
    county: "Denton County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Denton",
    recordingOffice: "Denton County Clerk",
    publicGis: true,
    intro:
      "Denton County wraps around Lewisville Lake and a long stretch of Corps of Engineers shoreline, so flowage easements and lake access rights sit next to fast growing suburban subdivisions.",
    commonEasements: [
      "Corps of Engineers flowage and shoreline easements",
      "Electric transmission corridors crossing former ranchland",
      "Subdivision drainage and detention pond easements",
      "Shared rural access easements off farm to market roads",
    ],
    recorderUrl: "https://www.dentoncounty.gov/166/County-Clerk",
    feeNote:
      "Recording is billed at the Texas first page rate with a reduced rate for later pages, and the Clerk publishes the current schedule online.",
    localNotes: [
      "Flowage easements allow periodic flooding and prohibit structures even where the ground is normally dry.",
      "Rural access easements written in the 1970s often describe a route by fence line rather than by survey.",
    ],
  },
  {
    slug: "allegheny",
    county: "Allegheny County",
    stateCode: "PA",
    stateName: "Pennsylvania",
    metro: "Pittsburgh",
    recordingOffice: "Allegheny County Department of Real Estate",
    publicGis: true,
    intro:
      "Pittsburgh's hillsides, river valleys and coal mining history give Allegheny County a distinctive title picture, where subsidence rights and stepped hillside access sit beside ordinary utility strips.",
    commonEasements: [
      "Coal and mineral subsidence rights reserved in old deeds",
      "Hillside stairway and stepped pedestrian access easements",
      "Sanitary and combined sewer easements crossing rear yards",
      "Duquesne Light distribution and transmission easements",
    ],
    recorderUrl: "https://www.alleghenycounty.us/government/records/recorder-of-deeds",
    feeNote:
      "Pennsylvania recording is charged per document with page tiers, and the county collects state writ tax and local fees at the same counter.",
    localNotes: [
      "Deeds here commonly reserve the coal and the right to remove it, which is a mining right rather than a surface easement.",
      "Public hillside stairways are city infrastructure and can cross private ground on a right of way that is easy to miss.",
    ],
  },
  {
    slug: "oakland",
    county: "Oakland County",
    stateCode: "MI",
    stateName: "Michigan",
    metro: "Pontiac",
    recordingOffice: "Oakland County Register of Deeds",
    publicGis: true,
    intro:
      "Oakland County has more than four hundred inland lakes, so lake access easements for backlot owners are the defining local title issue, alongside county drain rights on almost every subdivision.",
    commonEasements: [
      "Backlot lake access and dock easements",
      "County drain and open watercourse easements",
      "Private road easements serving lakefront plats",
      "Sanitary sewer easements running between lots to the lake shore",
    ],
    recorderUrl: "https://www.oakgov.com/government/clerk-register-of-deeds/about-us",
    feeNote:
      "Michigan charges a flat per document recording fee statewide, so a long lake access agreement costs the same to record as a one page grant.",
    localNotes: [
      "Lake access easements rarely say how many boats or docks are allowed, which is the fight that ends up in court.",
      "County drain easements give the drain commissioner access for machinery and spoil storage along the watercourse.",
    ],
  },
  {
    slug: "kent",
    county: "Kent County",
    stateCode: "MI",
    stateName: "Michigan",
    metro: "Grand Rapids",
    recordingOffice: "Kent County Register of Deeds",
    publicGis: true,
    intro:
      "Grand Rapids and the townships around it grew outward across orchard and farm ground, so Kent County deeds mix township drain easements with utility corridors and shared farm lane access.",
    commonEasements: [
      "Township and county drain easements across former orchards",
      "Consumers Energy distribution and pipeline easements",
      "Shared farm lane and split parcel access easements",
      "Storm water detention easements in newer townships plats",
    ],
    recorderUrl: "https://www.accesskent.com/Departments/RegisterOfDeeds/",
    feeNote:
      "Recording in Michigan is a flat fee per instrument, and the Register of Deeds requires a tax certification before certain conveyances are accepted.",
    localNotes: [
      "Land divisions from a parent farm parcel often rely on an access easement that was never recorded separately.",
      "Drain easements can be enlarged by the drain commissioner through a petition process rather than a new grant.",
    ],
  },
  {
    slug: "palm-beach",
    county: "Palm Beach County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "West Palm Beach",
    recordingOffice: "Palm Beach County Clerk of the Circuit Court and Comptroller",
    publicGis: true,
    intro:
      "Palm Beach County drains through a dense network of canals run by water control districts, so canal maintenance easements and lake maintenance easements behind homes are the norm rather than the exception.",
    commonEasements: [
      "Water control district canal and lake maintenance easements",
      "FPL electric distribution and transmission easements",
      "Golf course and country club access and cart path easements",
      "Platted utility and drainage easements in gated communities",
    ],
    recorderUrl: "https://www.mypalmbeachclerk.com/",
    feeNote:
      "Florida recording is charged per page with a higher first page rate, and documentary stamp tax is calculated separately on instruments that transfer an interest for consideration.",
    localNotes: [
      "Lake maintenance easements behind waterfront homes are usually twenty feet wide and prohibit fences and slabs.",
      "Canal easements held by drainage districts allow mowing and dredging equipment along the bank.",
    ],
  },
  {
    slug: "pima",
    county: "Pima County",
    stateCode: "AZ",
    stateName: "Arizona",
    metro: "Tucson",
    recordingOffice: "Pima County Recorder",
    publicGis: true,
    intro:
      "Tucson development pushes against federal land and desert washes, so Pima title work centers on wash and floodplain easements, buffer overlays and access across state trust and federal parcels.",
    commonEasements: [
      "Desert wash, floodplain and erosion hazard easements",
      "Tucson Electric Power distribution and transmission easements",
      "Access easements across state trust or federal land",
      "Conservation and buffer overlay easements near preserves",
    ],
    recorderUrl: "https://www.recorder.pima.gov/",
    feeNote:
      "Arizona charges a flat statutory recording fee per document rather than by the page, which keeps long surveyed exhibits affordable to record.",
    localNotes: [
      "Wash easements restrict grading and fencing because the wash must keep conveying storm flow.",
      "Access across state trust land runs on a permit or lease that can expire, not on a permanent recorded easement.",
    ],
  },
  {
    slug: "summit",
    county: "Summit County",
    stateCode: "OH",
    stateName: "Ohio",
    metro: "Akron",
    recordingOffice: "Summit County Fiscal Office",
    publicGis: true,
    intro:
      "Akron and the Cuyahoga Valley put Summit County lots against park land, former rail beds and steep ravines, so recreation trail easements and slope rights appear alongside ordinary utility strips.",
    commonEasements: [
      "Towpath and rail trail recreation easements",
      "Sanitary and storm sewer easements crossing rear yards",
      "Slope and ravine stabilization easements",
      "Ohio Edison distribution and street lighting easements",
    ],
    recorderUrl: "https://fiscaloffice.summitoh.net/",
    feeNote:
      "Ohio counties charge a base recording fee for the first two pages plus a per page rate after that, along with a fee for conveyance standards review.",
    localNotes: [
      "Former rail corridors near the valley can carry retained utility rights even after conversion to trail.",
      "Ravine slope easements often ban tree removal because the trees hold the bank.",
    ],
  },
  {
    slug: "san-francisco",
    county: "San Francisco County",
    stateCode: "CA",
    stateName: "California",
    metro: "San Francisco",
    recordingOffice: "San Francisco Assessor-Recorder",
    publicGis: true,
    intro:
      "In a city with almost no vacant land, San Francisco easement questions are about light wells, shared stairs and decks, and party wall rights between buildings that touch on both sides.",
    commonEasements: [
      "Party wall and shared foundation easements between attached buildings",
      "Light well and shaft easements between narrow lots",
      "Shared stair, deck and rear yard access easements",
      "Encroachment agreements for bay windows and stoops over the public way",
    ],
    recorderUrl: "https://www.sf.gov/departments/assessor-recorder",
    feeNote:
      "The Assessor-Recorder charges the California first page and per page fees plus city transfer tax filings handled at the time of recording.",
    localNotes: [
      "Encroachment permits for stoops and bay windows are revocable city permissions, not permanent property rights.",
      "Condominium conversions commonly create exclusive use easements over roof decks and parking that are easy to miss in the deed.",
    ],
  },
  {
    slug: "fresno",
    county: "Fresno County",
    stateCode: "CA",
    stateName: "California",
    metro: "Fresno",
    recordingOffice: "Fresno County Clerk-Recorder",
    publicGis: true,
    intro:
      "Fresno County is farm country with a growing city in the middle, so irrigation district canals, well and pump site rights and farm road access dominate rural title chains and follow the land into new subdivisions.",
    commonEasements: [
      "Irrigation district canal, lateral and standpipe easements",
      "Well site, pump and pipeline easements serving adjoining farms",
      "Farm road and field access easements between split parcels",
      "PG&E distribution lines crossing agricultural ground",
    ],
    recorderUrl: "https://www.fresnocountyca.gov/Departments/County-Clerk-Registrar-of-Voters",
    feeNote:
      "California recording is priced per page with a higher first page charge, plus the state surcharge that applies to most instruments outside a residential sale.",
    localNotes: [
      "Canal and lateral easements carry a right of access for maintenance equipment along both banks.",
      "When a farm is split, the recorded access easement often follows a field road that shifts with cultivation.",
    ],
  },
  {
    slug: "ventura",
    county: "Ventura County",
    stateCode: "CA",
    stateName: "California",
    metro: "Ventura",
    recordingOffice: "Ventura County Clerk-Recorder",
    publicGis: true,
    intro:
      "Between the coast, the citrus belt and steep chaparral canyons, Ventura County title work mixes agricultural preserve contracts, debris flow channels and shared canyon road access.",
    commonEasements: [
      "Flood control district channel and debris basin easements",
      "Agricultural preserve and open space restrictions",
      "Private canyon road and fire access easements",
      "Oil field pipeline and access easements in the older producing areas",
    ],
    recorderUrl: "https://recorder.countyofventura.org/",
    feeNote:
      "The Clerk-Recorder applies the standard California first page and additional page fees and publishes formatting rules that documents must meet to be accepted.",
    localNotes: [
      "Canyon road easements frequently lack any maintenance formula, which becomes an issue after each storm season.",
      "Old oil leases can leave surface access rights that survive long after production stops.",
    ],
  },
  {
    slug: "kern",
    county: "Kern County",
    stateCode: "CA",
    stateName: "California",
    metro: "Bakersfield",
    recordingOffice: "Kern County Assessor-Recorder",
    publicGis: true,
    intro:
      "Kern County runs on oil, wind, solar and irrigated agriculture, so surface use agreements, gathering lines and renewable energy corridors show up in title far more often than ordinary subdivision strips.",
    commonEasements: [
      "Oil and gas surface use and gathering line easements",
      "Wind and solar project access and collection line corridors",
      "Water district canal and aqueduct easements",
      "Section line and rural access easements on desert and farm parcels",
    ],
    recorderUrl: "https://www.kerncounty.com/government/county-departments/assessor-recorder",
    feeNote:
      "Recording follows the California per page structure, with additional state surcharges that apply to documents unrelated to a residential sale.",
    localNotes: [
      "A severed mineral estate can bring surface access rights with it, even without a separate recorded easement.",
      "Renewable project easements are often long term leases with recorded memoranda rather than full grants.",
    ],
  },
  {
    slug: "fort-bend",
    county: "Fort Bend County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Sugar Land",
    recordingOffice: "Fort Bend County Clerk",
    publicGis: true,
    intro:
      "Fort Bend grew through municipal utility districts on flat coastal prairie, so MUD water and sewer easements and detention pond rights define the local title picture.",
    commonEasements: [
      "Municipal utility district water, sewer and lift station easements",
      "Regional detention and drainage easements",
      "Pipeline corridors crossing master planned communities",
      "Landscape, screening and reserve access easements",
    ],
    recorderUrl: "https://www.fortbendcountytx.gov/government/departments/county-clerk",
    feeNote:
      "Texas clerks charge a first page rate with a reduced per page rate afterward, and plats are filed on a separate fee schedule.",
    localNotes: [
      "Utility district easements are recorded by the district, so they may not appear under a homeowner name search.",
      "Detention reserves next to homes are usually owned in common and burdened with a district maintenance right.",
    ],
  },
  {
    slug: "williamson",
    county: "Williamson County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Round Rock",
    recordingOffice: "Williamson County Clerk",
    publicGis: true,
    intro:
      "North of Austin, Williamson County is converting ranchland into subdivisions on karst limestone, so cave and recharge feature protection easements sit alongside fast expanding utility corridors.",
    commonEasements: [
      "Karst feature and cave preserve conservation easements",
      "Water line and wastewater interceptor easements on former ranchland",
      "Roadway expansion and right of way dedications",
      "Electric transmission corridors serving new growth",
    ],
    recorderUrl: "https://www.wilcotx.gov/206/County-Clerk",
    feeNote:
      "The County Clerk charges a first page fee plus a lower rate for each additional page, with formatting and margin rules enforced at intake.",
    localNotes: [
      "Karst protection easements can prohibit construction within a set radius of a recharge feature.",
      "Road widening dedications from the ranch era are sometimes recorded but never fenced, so the usable frontage is smaller than it looks.",
    ],
  },
  {
    slug: "montgomery",
    county: "Montgomery County",
    stateCode: "TX",
    stateName: "Texas",
    metro: "Conroe",
    recordingOffice: "Montgomery County Clerk",
    publicGis: true,
    intro:
      "Lake Conroe, the Sam Houston National Forest and heavy pipeline traffic give Montgomery County title chains a mix of shoreline rights, forest access and energy corridors.",
    commonEasements: [
      "Lake Conroe shoreline and flowage easements held by the river authority",
      "Crude and natural gas pipeline corridors",
      "Forest and timber tract access easements",
      "Subdivision drainage and utility easements in wooded plats",
    ],
    recorderUrl: "https://www.mctx.org/departments/departments_a_-_c/county_clerk/index.php",
    feeNote:
      "Recording is billed at the Texas first page rate with a smaller charge for later pages, so a long exhibit adds to the filing cost.",
    localNotes: [
      "Shoreline rights on the lake are controlled by the river authority and limit docks and bulkheads regardless of deed language.",
      "Timber tract access easements often predate the subdivision and are described by bearing and distance only.",
    ],
  },
  {
    slug: "pinellas",
    county: "Pinellas County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "St. Petersburg",
    recordingOffice: "Pinellas County Clerk of the Circuit Court and Comptroller",
    publicGis: true,
    intro:
      "Pinellas is a built out peninsula, so the recurring easement questions involve seawalls, coastal construction limits, redevelopment on narrow lots and utility strips platted in the 1950s.",
    commonEasements: [
      "Seawall and shoreline maintenance easements",
      "Coastal construction control line restrictions on beachfront parcels",
      "Platted rear utility easements in mid century subdivisions",
      "Stormwater and outfall easements to the bay and gulf",
    ],
    recorderUrl: "https://www.mypinellasclerk.gov/",
    feeNote:
      "Florida charges a higher first page rate plus a per page rate, with documentary stamp tax assessed separately on transfers for consideration.",
    localNotes: [
      "Mid century plats show narrow rear utility easements that are still active even where poles were removed.",
      "Coastal setback lines restrict building seaward of them regardless of where the lot line sits.",
    ],
  },
  {
    slug: "duval",
    county: "Duval County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Jacksonville",
    recordingOffice: "Duval County Clerk of the Circuit Court",
    publicGis: true,
    intro:
      "Jacksonville's consolidated city and county government spans marshland, the St. Johns River and a large port, so marsh conservation, riverfront access and port related rail easements all appear in local title work.",
    commonEasements: [
      "Salt marsh and wetland conservation easements",
      "Riverfront dock, bulkhead and riparian access easements",
      "JEA electric, water and sewer utility easements",
      "Rail spur and port access corridors in industrial areas",
    ],
    recorderUrl: "https://www.duvalclerk.com/",
    feeNote:
      "Recording fees follow the Florida first page and per page structure, and the Clerk collects documentary stamps on qualifying instruments at filing.",
    localNotes: [
      "Wetland conservation easements are often required as mitigation and bar clearing even on high ground nearby.",
      "Riparian rights are not the same as a recorded dock easement, and the two are regularly confused at closing.",
    ],
  },
  {
    slug: "lee",
    county: "Lee County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Fort Myers",
    recordingOffice: "Lee County Clerk of Court and Comptroller",
    publicGis: true,
    intro:
      "Lee County was platted in enormous waterfront and interior developments, so canal maintenance, gulf access and unbuilt platted road rights are the issues that decide whether a lot is usable.",
    commonEasements: [
      "Canal bank maintenance easements in waterfront developments",
      "Gulf access and navigation easements over private canals",
      "Unbuilt platted road rights of way in older interior plats",
      "Drainage district ditch and swale easements",
    ],
    recorderUrl: "https://www.leeclerk.org/",
    feeNote:
      "The Clerk charges the Florida statutory first page and additional page fees, with documentary stamp tax handled at the same time as recording.",
    localNotes: [
      "Some interior plats were never built, so the paper street next to a lot may still be a recorded public right.",
      "Canal maintenance easements restrict fences, slabs and landscaping along the seawall cap.",
    ],
  },
  {
    slug: "polk",
    county: "Polk County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Lakeland",
    recordingOffice: "Polk County Clerk of the Circuit Court",
    publicGis: true,
    intro:
      "Between Tampa and Orlando, Polk County layers phosphate mining history, citrus groves and rapid subdivision growth, which produces reclaimed land restrictions next to brand new utility easements.",
    commonEasements: [
      "Phosphate mining reclamation and settling area restrictions",
      "Citrus grove irrigation and access easements",
      "Lake and chain of lakes drainage easements",
      "New subdivision utility, drainage and landscape easements",
    ],
    recorderUrl: "https://www.polkcountyclerk.net/",
    feeNote:
      "Florida recording is charged per page with a higher first page rate, and the Clerk collects the applicable documentary stamp tax on transfers.",
    localNotes: [
      "Reclaimed mining land can carry recorded restrictions on foundations and excavation.",
      "Grove irrigation easements often survive the conversion of the grove into house lots.",
    ],
  },
  {
    slug: "brevard",
    county: "Brevard County",
    stateCode: "FL",
    stateName: "Florida",
    metro: "Melbourne",
    recordingOffice: "Brevard County Clerk of the Circuit Court",
    publicGis: true,
    intro:
      "The Space Coast runs between the Indian River Lagoon and the Atlantic, so lagoon setbacks, dune protection and launch related overflight restrictions shape what a Brevard lot can actually be used for.",
    commonEasements: [
      "Indian River Lagoon shoreline and dock easements",
      "Dune and coastal protection easements on barrier island lots",
      "Drainage canal easements run by the water management district",
      "Overflight and safety zone restrictions near launch and airfield operations",
    ],
    recorderUrl: "https://www.brevardclerk.us/",
    feeNote:
      "Fees follow the Florida statutory model of a first page charge plus additional pages, with stamps calculated separately on qualifying transfers.",
    localNotes: [
      "Dune easements prohibit walkovers and structures seaward of the line even on private beachfront.",
      "Lagoon docks require permits that are separate from any recorded shoreline easement.",
    ],
  },
  {
    slug: "monroe",
    county: "Monroe County",
    stateCode: "NY",
    stateName: "New York",
    metro: "Rochester",
    recordingOffice: "Monroe County Clerk",
    publicGis: true,
    intro:
      "Rochester's older neighborhoods and the townships around them carry driveway, sewer and canal era easements, with Erie Canal and rail corridors threading through suburban plats.",
    commonEasements: [
      "Shared driveway easements between narrow city lots",
      "Erie Canal and former rail corridor rights of way",
      "Sanitary sewer and storm drain easements across rear yards",
      "RG&E electric and gas distribution easements",
    ],
    recorderUrl: "https://www.monroecounty.gov/clerk",
    feeNote:
      "The County Clerk charges a per instrument recording fee plus a page charge, and generated cover pages count toward the page total.",
    localNotes: [
      "Shared driveways in city neighborhoods are often used by agreement with nothing recorded at all.",
      "Canal and towpath corridors can carry state rights that limit building close to the bank.",
    ],
  },
  {
    slug: "baltimore-county",
    county: "Baltimore County",
    stateCode: "MD",
    stateName: "Maryland",
    metro: "Towson",
    recordingOffice: "Circuit Court for Baltimore County, Land Records",
    publicGis: true,
    intro:
      "Baltimore County surrounds the city without including it, so land records run from dense inner suburbs out to protected watershed and agricultural preservation ground north of the beltway.",
    commonEasements: [
      "Agricultural land preservation easements on northern farms",
      "Reservoir watershed protection and forest buffer easements",
      "Shared driveway and private lane easements in older suburbs",
      "BGE electric and gas distribution easements",
    ],
    recorderUrl: "https://www.baltimorecountymd.gov/departments/circuit-court/land-records",
    feeNote:
      "Maryland records land instruments through the Circuit Court, where a recording fee applies per instrument and recordation tax is collected before filing.",
    localNotes: [
      "Preservation easements are permanent and limit the number of dwellings a farm can ever support.",
      "Forest conservation easements from a subdivision approval are recorded separately from the plat.",
    ],
  },
  {
    slug: "hamilton",
    county: "Hamilton County",
    stateCode: "OH",
    stateName: "Ohio",
    metro: "Cincinnati",
    recordingOffice: "Hamilton County Recorder",
    publicGis: true,
    intro:
      "Cincinnati's hillsides and river terraces mean Hamilton County lots often depend on stepped access, retaining structures and hillside stabilization rights as much as on utility strips.",
    commonEasements: [
      "Hillside stabilization and retaining wall easements",
      "Public stairway and stepped pedestrian access rights",
      "Sanitary and combined sewer easements crossing back yards",
      "Duke Energy electric and gas distribution easements",
    ],
    recorderUrl: "https://www.hamiltoncountyohio.gov/government/departments/recorder",
    feeNote:
      "Ohio counties charge a base fee covering the first two pages plus a per page rate afterward, along with conveyance standards review by the auditor.",
    localNotes: [
      "Retaining wall responsibility is often assigned by an old easement rather than by who owns the wall today.",
      "Combined sewer easements can carry access rights for large maintenance equipment through a narrow side yard.",
    ],
  },
  {
    slug: "bucks",
    county: "Bucks County",
    stateCode: "PA",
    stateName: "Pennsylvania",
    metro: "Doylestown",
    recordingOffice: "Bucks County Recorder of Deeds",
    publicGis: true,
    intro:
      "Bucks County stretches from Philadelphia suburbs to Delaware River farmland, so agricultural security easements, historic lane access and floodplain restrictions all appear in local title work.",
    commonEasements: [
      "Agricultural conservation easements on preserved farms",
      "Delaware River floodplain and riparian buffer restrictions",
      "Historic private lane and shared access easements",
      "PECO electric distribution and transmission easements",
    ],
    recorderUrl: "https://www.buckscounty.gov/168/Recorder-of-Deeds",
    feeNote:
      "Pennsylvania recorders charge per document with page tiers and collect state and local realty transfer tax on qualifying instruments at filing.",
    localNotes: [
      "Preserved farm easements are permanent and restrict subdivision as well as construction.",
      "Colonial era lanes are sometimes described by monuments that no longer exist, which makes a survey essential.",
    ],
  },
  {
    slug: "montgomery",
    county: "Montgomery County",
    stateCode: "PA",
    stateName: "Pennsylvania",
    metro: "Norristown",
    recordingOffice: "Montgomery County Recorder of Deeds",
    publicGis: true,
    intro:
      "Montgomery County's mature suburbs sit on creek watersheds and former industrial corridors, so stormwater management easements and rail trail conversions are frequent title issues.",
    commonEasements: [
      "Stormwater basin and management facility easements",
      "Rail trail and former railroad corridor rights of way",
      "Township sanitary sewer easements crossing residential lots",
      "Creek riparian buffer and floodplain restrictions",
    ],
    recorderUrl: "https://www.montcopa.org/126/Recorder-of-Deeds",
    feeNote:
      "Recording is charged per document with page based tiers, and realty transfer tax is collected by the Recorder at the time of filing.",
    localNotes: [
      "Stormwater basin easements come with an owner maintenance obligation enforced by the township.",
      "Trail conversions can retain utility rights inside the old rail corridor.",
    ],
  },
  {
    slug: "cobb",
    county: "Cobb County",
    stateCode: "GA",
    stateName: "Georgia",
    metro: "Marietta",
    recordingOffice: "Cobb County Superior Court Clerk, Real Estate Division",
    publicGis: true,
    intro:
      "Northwest of Atlanta, Cobb County subdivisions were cut into rolling terrain, so sewer easements down creek valleys and steep slope drainage rights are the recurring issues on residential lots.",
    commonEasements: [
      "Sanitary sewer easements following creek valleys",
      "Detention pond and storm drainage easements",
      "Georgia Power transmission and distribution corridors",
      "Subdivision entrance, landscape and sign easements",
    ],
    recorderUrl: "https://www.cobbcounty.org/courts/superior/clerk",
    feeNote:
      "Georgia records deeds through the Superior Court Clerk at a flat statutory filing fee per instrument, with transfer tax paid on conveyances.",
    localNotes: [
      "Sewer easements along creeks are wide and bar sheds, pools and fill inside the strip.",
      "Entrance and sign easements bind individual corner lots to association maintenance duties.",
    ],
  },
  {
    slug: "lake",
    county: "Lake County",
    stateCode: "IL",
    stateName: "Illinois",
    metro: "Waukegan",
    recordingOffice: "Lake County Recorder of Deeds",
    publicGis: true,
    intro:
      "Between Lake Michigan and the Chain O'Lakes, Lake County title work involves lake access rights for interior lots, wetland buffers and drainage district ditches inherited from farm drainage.",
    commonEasements: [
      "Lake access and pier easements for non riparian lots",
      "Wetland buffer and conservation easements",
      "Farm drainage tile and ditch easements",
      "ComEd transmission and distribution easements",
    ],
    recorderUrl: "https://www.lakecountyil.gov/155/Recorder",
    feeNote:
      "Illinois counties charge a predictable per document recording fee that bundles pages up to a set limit, with a surcharge structure set by ordinance.",
    localNotes: [
      "Drainage tile easements are frequently unrecorded and only show up when a tile is cut during construction.",
      "Lake access easements rarely specify pier size, which is the most common source of disputes here.",
    ],
  },
  {
    slug: "will",
    county: "Will County",
    stateCode: "IL",
    stateName: "Illinois",
    metro: "Joliet",
    recordingOffice: "Will County Clerk, Recordings Division",
    publicGis: true,
    intro:
      "Will County is the freight hub of the region, so intermodal rail, pipeline corridors and quarry haul routes cross the same farmland that is being subdivided for housing.",
    commonEasements: [
      "Intermodal rail and spur corridor easements",
      "Interstate pipeline and product line corridors",
      "Farm drainage district ditch and tile easements",
      "Subdivision stormwater detention easements",
    ],
    recorderUrl: "https://www.willcountyillinois.com/county-offices/elected-officials/county-clerk",
    feeNote:
      "Recording is charged as a flat fee per document covering a set number of pages, with additional charges beyond that page count.",
    localNotes: [
      "Pipeline corridors here are often multiple lines side by side under a single wide easement.",
      "Land in transition from farming still carries drainage district rights that survive the rezoning.",
    ],
  },
];

/** "Maricopa County" in Arizona -> "/easement-guides/arizona/maricopa" */
export function countyGuidePath(guide: CountyEasementGuide): string {
  return `/easement-guides/${guide.stateName.toLowerCase().replace(/\s+/g, "-")}/${guide.slug}`;
}

export function findCountyGuide(
  stateSlugValue: string | undefined,
  countySlug: string | undefined,
): CountyEasementGuide | undefined {
  if (!stateSlugValue || !countySlug) return undefined;
  const state = stateSlugValue.trim().toLowerCase();
  const county = countySlug.trim().toLowerCase();
  return COUNTY_EASEMENT_GUIDES.find(
    (guide) =>
      guide.stateName.toLowerCase().replace(/\s+/g, "-") === state && guide.slug === county,
  );
}

/** Guides grouped by state name, alphabetically. */
export function guidesByState(): { stateName: string; guides: CountyEasementGuide[] }[] {
  const map = new Map<string, CountyEasementGuide[]>();
  for (const guide of COUNTY_EASEMENT_GUIDES) {
    const list = map.get(guide.stateName) ?? [];
    list.push(guide);
    map.set(guide.stateName, list);
  }
  return Array.from(map.entries())
    .map(([stateName, guides]) => ({
      stateName,
      guides: [...guides].sort((a, b) => a.county.localeCompare(b.county)),
    }))
    .sort((a, b) => a.stateName.localeCompare(b.stateName));
}

/**
 * Matches a geocoder's county display name ("Travis County", "Baltimore city")
 * plus a state name or code to a guide, so an address lookup can link straight
 * to the right county guide. Returns undefined when we have no guide rather
 * than guessing.
 */
export function findCountyGuideByName(
  countyName: string | undefined,
  state: string | undefined,
): CountyEasementGuide | undefined {
  if (!countyName || !state) return undefined;
  const countyKey = countyName
    .toLowerCase()
    .replace(/\b(city and county|county|parish|borough|census area|municipality|city)\b/g, "")
    .replace(/[^a-z]/g, "");
  const stateKey = state.trim().toLowerCase();
  if (!countyKey || !stateKey) return undefined;
  return COUNTY_EASEMENT_GUIDES.find(
    (guide) =>
      (guide.stateName.toLowerCase() === stateKey ||
        guide.stateCode.toLowerCase() === stateKey) &&
      guide.slug.replace(/[^a-z]/g, "") === countyKey,
  );
}
